import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Heart,
  Sparkles,
  BookOpen,
  Utensils,
  Droplets,
  Dumbbell,
  Star,
  Flower2,
  Home,
  Shield,
  CookingPot,
  FileText,
  Users,
  Pill,
  User,
  Calendar
} from "lucide-react";
import PartnerDashboard from "./PartnerDashboard";
import HistoryView from "./HistoryView";
import ProfileSection from "./ProfileSection";
import { useAuth } from "./AuthContext";
import { 
  saveProgress, 
  saveWaterIntake,
  getWaterIntake,
  getNotes
} from "./partnerApi";
import { supabase } from "./supabaseClient";
import CompanionChat from "./CompanionChat";
import DoctorPrep from "./DoctorPrep";
import MoodCheck from "./MoodCheck";
import PeriodTracker from "./PeriodTracker";
import { NavButton, QuestCard } from "./UIComponents";

const BREAKFASTS = [
  {
    title: "Berry Yogurt Power Bowl",
    why: "Protein + fiber + healthy fats can help keep energy steadier.",
    items: ["Greek yogurt", "berries", "chia seeds", "walnuts"],
  },
  {
    title: "Egg & Avocado Toast",
    why: "A balanced breakfast with protein and healthy fats.",
    items: ["2 eggs", "whole-grain toast", "avocado", "pepper flakes"],
  },
  {
    title: "Oats That Give Main Character Energy",
    why: "Oats and seeds add fiber, which many people with PCOS find helpful.",
    items: ["rolled oats", "cinnamon", "flax seeds", "apple slices"],
  },
  {
    title: "Besan Chilla Breakfast Shield",
    why: "Protein-rich and filling, with less sugar spike drama.",
    items: ["besan chilla", "mint chutney", "cucumber", "tea or water"],
  },
  {
    title: "Smoothie Sidekick",
    why: "Easy option for busy mornings when energy is low.",
    items: ["unsweetened milk", "spinach", "berries", "protein source"],
  },
];

const EXERCISES = [
  {
    title: "Princess Walk",
    subtitle: "20–30 minutes",
    why: "Regular movement supports insulin sensitivity and mood.",
    category: "cardio",
    difficulty: "easy",
    duration: "20-30 min",
    steps: [
      "Put on comfy shoes and your favorite music or podcast",
      "Walk at a pace where you can talk but feel slightly warm",
      "Aim for 20-30 minutes, but even 10 counts!",
      "Optional: find a pretty route with nature"
    ],
    benefits: ["Improves insulin sensitivity", "Boosts mood", "Low impact on joints"],
    pcosTip: "Morning walks can help regulate cortisol levels",
  },
  {
    title: "Resistance Band Quest",
    subtitle: "10–15 minutes",
    why: "Strength work can help support metabolism and overall fitness.",
    category: "strength",
    difficulty: "medium",
    duration: "10-15 min",
    steps: [
      "Start with arm pulls - hold band at chest height, pull apart",
      "Squat with band above knees - 12 reps",
      "Standing rows - anchor band, pull toward chest",
      "Lateral walks - band around ankles, step sideways"
    ],
    benefits: ["Builds lean muscle", "Boosts metabolism", "Easy home workout"],
    pcosTip: "Muscle tissue helps with blood sugar regulation",
  },
  {
    title: "Dance Battle vs PCOS",
    subtitle: "1 fun song minimum",
    why: "Movement counts even when it is goofy and joyful.",
    category: "cardio",
    difficulty: "easy",
    duration: "5-15 min",
    steps: [
      "Put on your absolute favorite upbeat song",
      "Close the door, let go of self-judgment",
      "Move however feels good - no rules!",
      "Add more songs if you're having fun"
    ],
    benefits: ["Stress relief", "Cardio without feeling like exercise", "Mood booster"],
    pcosTip: "Dancing releases endorphins that can help with mood swings",
  },
  {
    title: "Stretch + Breathe Reset",
    subtitle: "8–10 minutes",
    why: "A calmer nervous system can help daily consistency feel easier.",
    category: "flexibility",
    difficulty: "easy",
    duration: "8-10 min",
    steps: [
      "Neck rolls - slowly, 5 each direction",
      "Shoulder shrugs - up to ears, release, 10 times",
      "Cat-cow stretch - on all fours, 8 reps",
      "Child's pose - hold for 1 minute, deep breathing",
      "Seated forward fold - gentle stretch, 30 seconds"
    ],
    benefits: ["Reduces stress", "Improves flexibility", "Better sleep"],
    pcosTip: "Evening stretches can help with sleep quality",
  },
  {
    title: "Pilates Core Power",
    subtitle: "15–20 minutes",
    why: "Core strength supports better posture and reduced back pain.",
    category: "strength",
    difficulty: "medium",
    duration: "15-20 min",
    steps: [
      "Dead bug - opposite arm/leg, 10 each side",
      "Modified plank - hold 20-30 seconds, 3 times",
      "Bird dog - opposite arm/leg extension, 8 each side",
      "Glute bridges - 15 reps, slow and controlled",
      "Side-lying leg lifts - 12 each side"
    ],
    benefits: ["Core stability", "Better posture", "Back pain relief"],
    pcosTip: "Core strength helps during pregnancy later in life",
  },
  {
    title: "Yoga Flow for Hormones",
    subtitle: "20 minutes",
    why: "Specific yoga poses may help with hormone balance and stress.",
    category: "flexibility",
    difficulty: "easy",
    duration: "20 min",
    steps: [
      "Start in child's pose - 2 min, deep breaths",
      "Cat-cow flow - 2 min, sync with breath",
      "Downward dog - hold 1 min",
      "Warrior II - 1 min each side",
      "Supine twist - 1 min each side",
      "Legs up the wall - 5 min (amazing for circulation!)"
    ],
    benefits: ["Stress reduction", "Hormone balance", "Better circulation"],
    pcosTip: "Legs up the wall is especially helpful for circulation and relaxation",
  },
  {
    title: "HIIT for Busy Days",
    subtitle: "12 minutes",
    why: "Short bursts can be as effective as longer workouts.",
    category: "cardio",
    difficulty: "hard",
    duration: "12 min",
    steps: [
      "Warm up: march in place - 1 min",
      "Circuit (3 rounds, 30 sec work, 15 sec rest):",
      "  - Jumping jacks (or step jacks)",
      "  - Bodyweight squats",
      "  - Mountain climbers (modified OK)",
      "  - High knees",
      "Cool down: walk in place - 1 min"
    ],
    benefits: ["Efficient cardio", "Boosts metabolism for hours", "Time-saving"],
    pcosTip: "HIIT can improve insulin sensitivity, but don't overdo it if stressed",
  },
  {
    title: "Evening Wind-Down Walk",
    subtitle: "15–20 minutes after dinner",
    why: "Post-meal walks help with blood sugar and digestion.",
    category: "cardio",
    difficulty: "easy",
    duration: "15-20 min",
    steps: [
      "Wait 10-15 minutes after eating",
      "Walk at a gentle, relaxed pace",
      "No phone - just you and the walk",
      "Notice the sky, trees, neighborhood"
    ],
    benefits: ["Better digestion", "Blood sugar regulation", "Evening relaxation"],
    pcosTip: "Even a 10-minute walk after meals can reduce blood sugar spikes by 30%",
  },
  {
    title: "Bedtime Stretch Routine",
    subtitle: "5–10 minutes",
    why: "Gentle stretching before bed improves sleep quality.",
    category: "flexibility",
    difficulty: "easy",
    duration: "5-10 min",
    steps: [
      "Neck stretches - ear to shoulder, 30 sec each",
      "Seated spinal twist - 30 sec each side",
      "Butterfly stretch - 1 min, breathe deeply",
      "Knees to chest - 1 min, rock gently",
      "Happy baby pose - 1 min, optional"
    ],
    benefits: ["Better sleep", "Releases tension", "Calms mind"],
    pcosTip: "Quality sleep is crucial for hormone regulation",
  },
  {
    title: "Stair Challenge",
    subtitle: "5–10 minutes",
    why: "Stairs are an easy way to get cardio without equipment.",
    category: "cardio",
    difficulty: "medium",
    duration: "5-10 min",
    steps: [
      "Find a staircase (home, park, mall)",
      "Walk up and down at comfortable pace",
      "Start with 5 minutes, build up",
      "Optional: every other step for more intensity"
    ],
    benefits: ["Cardiovascular health", "Leg strength", "Accessible anywhere"],
    pcosTip: "Taking stairs throughout the day adds up!",
  },
  {
    title: "Bodyweight Strength Circuit",
    subtitle: "15–20 minutes",
    why: "Building muscle helps with metabolism and insulin sensitivity.",
    category: "strength",
    difficulty: "medium",
    duration: "15-20 min",
    steps: [
      "Warm up: arm circles, leg swings - 2 min",
      "Squats - 3 sets of 12",
      "Push-ups (wall/knees OK) - 3 sets of 8",
      "Lunges - 3 sets of 8 each leg",
      "Plank hold - 3 rounds, 20-30 sec",
      "Glute bridges - 3 sets of 15"
    ],
    benefits: ["Builds lean muscle", "No equipment needed", "Full body workout"],
    pcosTip: "Start with modified versions and progress over time",
  },
  {
    title: "Morning Energizer",
    subtitle: "5 minutes",
    why: "Quick morning movement sets a positive tone for the day.",
    category: "cardio",
    difficulty: "easy",
    duration: "5 min",
    steps: [
      "Reach for the sky - stretch tall, 5 times",
      "Arm circles - 10 forward, 10 back",
      "Hip circles - 10 each direction",
      "Jumping jacks (or marching) - 30 seconds",
      "Shake it out - whole body shake, 30 seconds"
    ],
    benefits: ["Wakes up body", "Boosts energy", "Quick mood lift"],
    pcosTip: "Morning movement helps regulate circadian rhythm",
  },
];

const EXERCISE_CATEGORIES = [
  { key: "all", name: "All Exercises", icon: "🏃‍♀️" },
  { key: "cardio", name: "Cardio", icon: "❤️" },
  { key: "strength", name: "Strength", icon: "💪" },
  { key: "flexibility", name: "Flexibility & Yoga", icon: "🧘‍♀️" },
];

// These constants are used in the tips section
// These constants are for future features
/* eslint-disable no-unused-vars */
const FOODS_TO_ENJOY = [
  "Eggs",
  "Greek yogurt or unsweetened yogurt",
  "Fish or chicken",
  "Lentils and beans",
  "Leafy greens",
  "Berries",
  "Avocado",
  "Nuts and seeds",
  "Whole grains in balanced portions",
  "Olive oil",
];

const LIMIT_MORE_OFTEN = [
  "Sugary drinks",
  "Highly processed snacks",
  "Very sugary breakfast foods",
  "Frequent fast food",
  "Large portions of refined carbs on their own",
];

const PRODUCTS = [
  {
    name: "Resistance Bands",
    reason: "Cute, easy home workouts with low friction.",
  },
  {
    name: "Pill Organizer",
    reason: "Helps with supplement consistency if prescribed or recommended.",
  },
  {
    name: "Large Water Bottle",
    reason: "Makes hydration easier without thinking too much.",
  },
  {
    name: "Spearmint Tea",
    reason: "Some people enjoy it as a calming routine, but it is not a cure.",
  },
];

const GUIDE_SECTIONS = [
  {
    key: "basics",
    title: "PCOS Basics",
    text: "PCOS cannot be promised away or magically reversed by one food, one workout, or one supplement. But many symptoms can improve a lot with steady habits, medical follow-up, movement, balanced meals, sleep, and stress support.",
  },
  {
    key: "periods",
    title: "Why Regular Cycles Matter",
    text: "Regular periods are a really positive sign. Tracking them helps notice changes early and can be helpful later when planning pregnancy.",
  },
  {
    key: "fertility",
    title: "Future Fertility",
    text: "PCOS does not mean ‘cannot have children.’ Many people with PCOS conceive naturally or with help later. The game plan now is to build strong habits and stay medically supported.",
  },
  {
    key: "care",
    title: "Medical Support",
    text: "A doctor or gynecologist can help with periods, hormone symptoms, screenings, and pregnancy planning. This app is a supportive guide, not a diagnosis tool.",
  },
];

// --- Cook Book Section ---
// Each category is an object with a name and a recipes array
const COOKBOOK_CATEGORIES = [
  {
    name: "Indian Breakfasts",
    recipes: [
      {
        name: "Besan Chilla (Gram Flour Pancake)",
        ingredients: [
          "1 cup besan (gram flour)",
          "½ chopped onion",
          "½ chopped tomato",
          "½ chopped spinach",
          "1 green chili (optional)",
          "Salt",
          "Turmeric",
          "Water",
          "Olive oil (small amount)"
        ],
        steps: [
          "Mix besan with water to form batter.",
          "Add vegetables and spices.",
          "Heat pan with little oil.",
          "Pour batter like pancake.",
          "Cook both sides till golden."
        ],
        why: [
          "High protein → reduces insulin spikes",
          "Low glycemic index → keeps blood sugar stable",
          "Fiber from veggies → improves gut health",
          "Helps reduce cravings later in the day"
        ]
      },
      {
        name: "Moong Dal Chilla",
        ingredients: [
          "Soaked moong dal",
          "Ginger",
          "Green chili",
          "Salt",
          "Spinach",
          "Oil (minimal)"
        ],
        steps: [
          "Blend soaked dal.",
          "Add vegetables.",
          "Cook like pancake."
        ],
        why: [
          "Very high protein",
          "Helps reduce insulin resistance",
          "Keeps hunger low",
          "Helps manage weight gain"
        ]
      },
      {
        name: "Oats with Cinnamon & Seeds",
        ingredients: [
          "½ cup rolled oats",
          "1 cup milk (unsweetened)",
          "Cinnamon",
          "Chia seeds",
          "Flax seeds",
          "Apple slices"
        ],
        steps: [
          "Cook oats in milk.",
          "Add cinnamon.",
          "Top with seeds and apple."
        ],
        why: [
          "High fiber → improves insulin resistance",
          "Cinnamon → helps regulate blood sugar",
          "Seeds → support hormone balance",
          "Helps reduce belly fat accumulation"
        ]
      },
      {
        name: "Egg & Vegetable Omelette",
        ingredients: [
          "2 eggs",
          "Spinach",
          "Mushrooms",
          "Tomatoes",
          "Onion",
          "Black pepper",
          "Olive oil"
        ],
        steps: [
          "Beat eggs.",
          "Add vegetables.",
          "Cook in pan with minimal oil.",
          "Serve hot."
        ],
        why: [
          "Protein-rich → improves insulin sensitivity",
          "Keeps you full longer",
          "Helps reduce overeating",
          "Supports muscle metabolism"
        ]
      },
      {
        name: "Greek Yogurt Berry Bowl",
        ingredients: [
          "Greek yogurt",
          "Blueberries",
          "Strawberries",
          "Chia seeds",
          "Walnuts"
        ],
        steps: [
          "Mix everything in bowl."
        ],
        why: [
          "High protein → stabilizes blood sugar",
          "Berries → anti-inflammatory",
          "Healthy fats → reduce cravings",
          "Supports gut microbiome"
        ]
      },
      {
        name: "Vegetable Oats Upma",
        ingredients: [
          "1 cup oats",
          "1/2 cup mixed vegetables (carrot, beans, peas)",
          "1 tsp mustard seeds",
          "Curry leaves",
          "Salt",
          "Pepper",
          "1 tsp oil"
        ],
        steps: [
          "Roast oats lightly and set aside.",
          "Heat oil, add mustard seeds and curry leaves.",
          "Add vegetables and sauté.",
          "Add oats, salt, pepper, and a splash of water.",
          "Cook until veggies are soft and oats are fluffy."
        ],
        why: [
          "Balanced carbs + fiber",
          "Reduces midday cravings",
          "Supports steady energy release"
        ]
      },
      {
        name: "Ragi Dosa",
        ingredients: [
          "1 cup ragi flour",
          "1/4 cup rice flour",
          "1/4 cup yogurt",
          "Salt",
          "Water",
          "Oil for cooking"
        ],
        steps: [
          "Mix ragi, rice flour, yogurt, salt, and water to make a thin batter.",
          "Pour on hot pan, spread thin, cook with little oil.",
          "Flip and cook both sides."
        ],
        why: [
          "Low glycemic index",
          "Rich in calcium and fiber",
          "Supports blood sugar control"
        ]
      },
      {
        name: "Multigrain Vegetable Paratha (minimal oil)",
        ingredients: [
          "1 cup multigrain flour",
          "1/2 cup grated vegetables (carrot, spinach, beetroot)",
          "Salt",
          "Spices",
          "Water",
          "Minimal oil"
        ],
        steps: [
          "Mix flour, veggies, salt, spices, and water to make dough.",
          "Roll into parathas.",
          "Cook on pan with minimal oil."
        ],
        why: [
          "High fiber",
          "More nutrients than plain wheat",
          "Keeps you full longer"
        ]
      },
      {
        name: "Paneer Bhurji with veggies",
        ingredients: [
          "Paneer (crumbled)",
          "Onion",
          "Tomato",
          "Bell pepper",
          "Spinach",
          "Spices",
          "Oil"
        ],
        steps: [
          "Sauté onion, tomato, bell pepper in oil.",
          "Add spinach and cook.",
          "Add crumbled paneer and spices, cook for 2-3 min."
        ],
        why: [
          "High protein",
          "Vegetables add fiber",
          "Supports muscle and hormone health"
        ]
      },
      {
        name: "Vegetable Poha (with peanuts)",
        ingredients: [
          "1 cup poha (flattened rice)",
          "1/2 cup mixed vegetables",
          "2 tbsp peanuts",
          "Mustard seeds",
          "Curry leaves",
          "Salt",
          "Turmeric",
          "Oil"
        ],
        steps: [
          "Rinse poha and drain.",
          "Heat oil, add mustard seeds, curry leaves, peanuts.",
          "Add veggies, sauté, add poha, salt, turmeric.",
          "Mix and cook for 2-3 min."
        ],
        why: [
          "Quick, filling, and light",
          "Peanuts add protein and healthy fat",
          "Vegetables add fiber"
        ]
      },
      {
        name: "Idli with sambar",
        ingredients: [
          "Idli batter (fermented rice & urad dal)",
          "Sambar (lentil & vegetable stew)"
        ],
        steps: [
          "Steam idlis from batter.",
          "Serve hot with sambar."
        ],
        why: [
          "Fermented foods support gut health",
          "Sambar adds protein and fiber"
        ]
      },
      {
        name: "Vegetable Uttapam",
        ingredients: [
          "Idli/dosa batter",
          "Chopped vegetables (onion, tomato, capsicum, carrot)",
          "Oil"
        ],
        steps: [
          "Pour batter on pan, top with veggies.",
          "Cook both sides with little oil."
        ],
        why: [
          "Balanced carbs and fiber",
          "Vegetables add vitamins and minerals"
        ]
      },
      {
        name: "Sprouted Moong Salad",
        ingredients: [
          "Sprouted moong beans",
          "Onion",
          "Tomato",
          "Cucumber",
          "Lemon juice",
          "Salt",
          "Pepper"
        ],
        steps: [
          "Mix all ingredients in a bowl.",
          "Serve fresh."
        ],
        why: [
          "High protein",
          "Improves insulin sensitivity",
          "Very filling and light"
        ]
      },
      {
        name: "Oats Idli",
        ingredients: [
          "Oats (powdered)",
          "Semolina",
          "Yogurt",
          "Vegetables",
          "Eno/fruit salt",
          "Salt"
        ],
        steps: [
          "Mix oats, semolina, yogurt, veggies, salt.",
          "Add Eno, steam in idli molds."
        ],
        why: [
          "Higher fiber than regular idli",
          "Supports blood sugar control"
        ]
      },
      {
        name: "Millet Khichdi",
        ingredients: [
          "Millet",
          "Lentils",
          "Vegetables",
          "Spices",
          "Salt"
        ],
        steps: [
          "Cook millet, lentils, veggies, and spices together until soft."
        ],
        why: [
          "Balanced protein + carbs",
          "Improves digestion",
          "Reduces inflammation"
        ]
      },
      {
        name: "Vegetable Dalia (Broken Wheat Porridge)",
        ingredients: [
          "Broken wheat (dalia)",
          "Vegetables",
          "Salt",
          "Spices"
        ],
        steps: [
          "Cook dalia and veggies with water, salt, and spices until soft."
        ],
        why: [
          "High fiber",
          "Keeps you full longer",
          "Supports gut health"
        ]
      },
      {
        name: "Palak (Spinach) Omelette",
        ingredients: [
          "Eggs",
          "Spinach",
          "Onion",
          "Salt",
          "Pepper",
          "Oil"
        ],
        steps: [
          "Beat eggs, add spinach, onion, salt, pepper.",
          "Cook in pan with little oil."
        ],
        why: [
          "High protein",
          "Iron-rich",
          "Supports hormone balance"
        ]
      },
      {
        name: "Egg Bhurji with vegetables",
        ingredients: [
          "Eggs",
          "Onion",
          "Tomato",
          "Bell pepper",
          "Spinach",
          "Spices",
          "Oil"
        ],
        steps: [
          "Sauté onion, tomato, bell pepper in oil.",
          "Add spinach and cook.",
          "Add eggs, scramble, cook through."
        ],
        why: [
          "Protein-rich",
          "Vegetables add fiber",
          "Keeps you full longer"
        ]
      },
      {
        name: "Paneer Stuffed Moong Chilla",
        ingredients: [
          "Moong dal batter",
          "Paneer (grated)",
          "Spices",
          "Oil"
        ],
        steps: [
          "Make chilla with moong dal batter.",
          "Stuff with paneer and spices, fold and cook."
        ],
        why: [
          "High protein",
          "Keeps hunger low",
          "Supports muscle health"
        ]
      },
      {
        name: "Vegetable Besan Toast",
        ingredients: [
          "Bread (whole grain)",
          "Besan batter",
          "Chopped vegetables",
          "Spices",
          "Oil"
        ],
        steps: [
          "Dip bread in besan batter with veggies and spices.",
          "Cook on pan with little oil."
        ],
        why: [
          "More protein than regular toast",
          "Vegetables add fiber"
        ]
      },
      {
        name: "Peanut Chutney + Idli",
        ingredients: [
          "Idli",
          "Peanut chutney (peanuts, spices, lemon)"
        ],
        steps: [
          "Serve idli with peanut chutney."
        ],
        why: [
          "Peanuts add protein and healthy fat",
          "Fermented idli supports gut health"
        ]
      },
      {
        name: "Millet Pongal",
        ingredients: [
          "Millet",
          "Moong dal",
          "Spices",
          "Salt"
        ],
        steps: [
          "Cook millet, dal, spices, and salt together until soft."
        ],
        why: [
          "Balanced protein + carbs",
          "Easy to digest",
          "Supports steady energy"
        ]
      },
      {
        name: "Chia Seed Yogurt Bowl (Indian style)",
        ingredients: [
          "Yogurt",
          "Chia seeds",
          "Fruit (banana, apple, berries)",
          "Nuts"
        ],
        steps: [
          "Mix yogurt, chia seeds, fruit, and nuts in a bowl."
        ],
        why: [
          "High protein",
          "Healthy fats",
          "Supports hormone balance"
        ]
      },
    ],
  },
  {
    name: "Indian Lunch/Dinner",
    recipes: [
      {
        name: "Palak Paneer",
        ingredients: [
          "Spinach",
          "Paneer",
          "Garlic",
          "Onion",
          "Tomato",
          "Spices"
        ],
        steps: [
          "Cook spinach and blend.",
          "Cook paneer with spices.",
          "Combine together."
        ],
        why: [
          "Iron-rich → reduces fatigue",
          "Paneer provides protein",
          "Spinach reduces inflammation",
          "Supports hormonal balance"
        ]
      },
      {
        name: "Paneer Tikka",
        ingredients: [
          "Paneer cubes",
          "Yogurt",
          "Spices",
          "Bell peppers",
          "Onion",
          "Oil"
        ],
        steps: [
          "Marinate paneer and veggies in yogurt and spices.",
          "Skewer and grill or bake until golden."
        ],
        why: [
          "High protein",
          "Grilled, not fried",
          "Yogurt adds probiotics"
        ]
      },
      {
        name: "Rajma (Kidney Beans Curry)",
        ingredients: [
          "Kidney beans",
          "Onion",
          "Tomato",
          "Garlic",
          "Spices"
        ],
        steps: [
          "Soak and cook kidney beans.",
          "Sauté onion, garlic, spices, add tomato.",
          "Add beans, simmer."
        ],
        why: [
          "High fiber",
          "Improves blood sugar control",
          "Helps reduce cholesterol",
          "Supports gut bacteria"
        ]
      },
      {
        name: "Chole (Chickpea Curry)",
        ingredients: [
          "Chickpeas",
          "Onion",
          "Tomato",
          "Ginger",
          "Garlic",
          "Spices"
        ],
        steps: [
          "Soak and cook chickpeas.",
          "Sauté onion, ginger, garlic, spices.",
          "Add tomato, cook down, add chickpeas, simmer."
        ],
        why: [
          "Protein-rich",
          "High fiber",
          "Supports hormone balance"
        ]
      },
      {
        name: "Dal Tadka",
        ingredients: [
          "Lentils",
          "Onion",
          "Tomato",
          "Garlic",
          "Spices",
          "Oil or ghee"
        ],
        steps: [
          "Cook lentils until soft.",
          "Prepare tadka (tempering) with onion, garlic, spices in oil/ghee.",
          "Pour tadka over dal."
        ],
        why: [
          "Plant protein",
          "Easy to digest",
          "Supports gut health"
        ]
      },
      {
        name: "Dal Makhani (moderate butter)",
        ingredients: [
          "Whole urad dal",
          "Kidney beans",
          "Tomato",
          "Onion",
          "Butter (moderate)",
          "Cream (optional)",
          "Spices"
        ],
        steps: [
          "Cook dals until soft.",
          "Sauté onion, tomato, spices, add dals.",
          "Simmer with butter and a little cream."
        ],
        why: [
          "High protein",
          "Rich, but use moderate butter",
          "Good for occasional treat"
        ]
      },
      {
        name: "Vegetable Khichdi",
        ingredients: [
          "Rice or millet",
          "Lentils",
          "Mixed vegetables",
          "Spices",
          "Salt"
        ],
        steps: [
          "Cook rice/millet, lentils, veggies, and spices together until soft."
        ],
        why: [
          "Balanced protein + carbs",
          "Easy to digest",
          "Reduces inflammation"
        ]
      },
      {
        name: "Vegetable Curry",
        ingredients: [
          "Mixed vegetables",
          "Onion",
          "Tomato",
          "Spices",
          "Oil"
        ],
        steps: [
          "Sauté onion, tomato, spices in oil.",
          "Add vegetables, cook until tender."
        ],
        why: [
          "High fiber",
          "Vitamins and minerals",
          "Supports gut health"
        ]
      },
      {
        name: "Baingan Bharta",
        ingredients: [
          "Eggplant",
          "Onion",
          "Tomato",
          "Garlic",
          "Spices",
          "Oil"
        ],
        steps: [
          "Roast eggplant, peel and mash.",
          "Sauté onion, tomato, garlic, spices, add eggplant, cook."
        ],
        why: [
          "Low calorie",
          "High fiber",
          "Supports weight management"
        ]
      },
      {
        name: "Mixed Vegetable Sabzi",
        ingredients: [
          "Carrot",
          "Beans",
          "Cauliflower",
          "Cabbage",
          "Spices",
          "Oil"
        ],
        steps: [
          "Chop and cook all vegetables with spices and oil."
        ],
        why: [
          "High fiber",
          "Reduces inflammation",
          "Supports gut microbiome"
        ]
      },
      {
        name: "Lauki (Bottle Gourd) Curry",
        ingredients: [
          "Bottle gourd",
          "Onion",
          "Tomato",
          "Garlic",
          "Spices",
          "Oil"
        ],
        steps: [
          "Cook all ingredients together."
        ],
        why: [
          "Low calorie → supports weight loss",
          "Helps reduce bloating",
          "Easy to digest"
        ]
      },
      {
        name: "Tinda Sabzi",
        ingredients: [
          "Tinda (apple gourd)",
          "Onion",
          "Tomato",
          "Spices",
          "Oil"
        ],
        steps: [
          "Cook tinda with onion, tomato, spices, and oil."
        ],
        why: [
          "Low calorie",
          "Supports digestion"
        ]
      },
      {
        name: "Bhindi Masala",
        ingredients: [
          "Okra (bhindi)",
          "Onion",
          "Tomato",
          "Spices",
          "Oil"
        ],
        steps: [
          "Sauté okra, onion, tomato, spices in oil until cooked."
        ],
        why: [
          "Low calorie",
          "High fiber",
          "Supports gut health"
        ]
      },
      {
        name: "Cabbage Stir Fry",
        ingredients: [
          "Cabbage",
          "Carrot",
          "Spices",
          "Oil"
        ],
        steps: [
          "Stir fry cabbage, carrot, and spices in oil."
        ],
        why: [
          "High fiber",
          "Low calorie",
          "Supports digestion"
        ]
      },
      {
        name: "Palak Dal",
        ingredients: [
          "Spinach",
          "Lentils",
          "Onion",
          "Tomato",
          "Spices"
        ],
        steps: [
          "Cook lentils, spinach, onion, tomato, and spices together."
        ],
        why: [
          "Iron-rich",
          "Plant protein",
          "Supports hormone health"
        ]
      },
      {
        name: "Tofu Curry",
        ingredients: [
          "Tofu",
          "Onion",
          "Tomato",
          "Spices",
          "Oil"
        ],
        steps: [
          "Cook tofu with onion, tomato, spices, and oil."
        ],
        why: [
          "Plant protein",
          "Supports hormone balance"
        ]
      },
      {
        name: "Mushroom Masala",
        ingredients: [
          "Mushrooms",
          "Onion",
          "Tomato",
          "Spices",
          "Oil"
        ],
        steps: [
          "Cook mushrooms, onion, tomato, and spices in oil."
        ],
        why: [
          "Low calorie",
          "Supports immune health"
        ]
      },
      {
        name: "Vegetable Sambar",
        ingredients: [
          "Lentils",
          "Mixed vegetables",
          "Sambar powder",
          "Tamarind",
          "Spices"
        ],
        steps: [
          "Cook lentils and veggies with sambar powder, tamarind, and spices."
        ],
        why: [
          "High fiber",
          "Plant protein",
          "Supports gut health"
        ]
      },
      {
        name: "Chicken Curry (low oil)",
        ingredients: [
          "Chicken",
          "Onion",
          "Tomato",
          "Spices",
          "Minimal oil"
        ],
        steps: [
          "Cook chicken with onion, tomato, spices, and minimal oil."
        ],
        why: [
          "Lean protein",
          "Supports muscle health"
        ]
      },
      {
        name: "Fish Curry",
        ingredients: [
          "Fish",
          "Garlic",
          "Tomato",
          "Spices"
        ],
        steps: [
          "Cook fish in gravy with garlic, tomato, and spices, and minimal oil."
        ],
        why: [
          "Rich in Omega-3",
          "Reduces inflammation",
          "Improves hormone balance"
        ]
      },
      {
        name: "Grilled Tandoori Chicken",
        ingredients: [
          "Chicken",
          "Yogurt",
          "Tandoori masala",
          "Lemon juice",
          "Spices"
        ],
        steps: [
          "Marinate chicken in yogurt, spices, lemon.",
          "Grill until cooked through."
        ],
        why: [
          "Lean protein",
          "Grilled, not fried",
          "Yogurt adds probiotics"
        ]
      },
      {
        name: "Vegetable Biryani (brown rice version)",
        ingredients: [
          "Brown rice",
          "Mixed vegetables",
          "Spices",
          "Yogurt",
          "Oil"
        ],
        steps: [
          "Cook rice and veggies with spices, yogurt, and oil."
        ],
        why: [
          "More fiber than white rice biryani",
          "Vegetables add vitamins and minerals"
        ]
      },
      {
        name: "Paneer & Vegetable Stir Fry",
        ingredients: [
          "Paneer cubes",
          "Bell peppers",
          "Spinach",
          "Garlic",
          "Olive oil"
        ],
        steps: [
          "Cook paneer and vegetables in pan with olive oil and garlic."
        ],
        why: [
          "High protein",
          "Helps reduce cravings",
          "Supports muscle metabolism"
        ]
      },
      {
        name: "Chicken Saag",
        ingredients: [
          "Chicken",
          "Spinach",
          "Garlic",
          "Spices"
        ],
        steps: [
          "Cook chicken and spinach together with garlic and spices."
        ],
        why: [
          "Iron-rich",
          "Helps reduce fatigue",
          "Supports hormone health"
        ]
      },
      {
        name: "Methi Chicken",
        ingredients: [
          "Chicken",
          "Fenugreek leaves (methi)",
          "Onion",
          "Tomato",
          "Spices",
          "Oil"
        ],
        steps: [
          "Cook chicken with methi, onion, tomato, spices, and oil."
        ],
        why: [
          "Fenugreek supports blood sugar control",
          "Lean protein"
        ]
      },
    ],
  },
  {
    name: "Indian Snacks",
    recipes: [
      {
        name: "Roasted Chana",
        ingredients: ["Roasted chana (whole or split)", "Spices (optional)"],
        steps: ["Eat as is or toss with spices."],
        why: ["High protein", "Low calorie", "Good for snacking"]
      },
      {
        name: "Peanuts",
        ingredients: ["Peanuts (roasted or boiled)", "Salt (optional)", "Spices (optional)"],
        steps: ["Eat as is or toss with salt/spices."],
        why: ["Healthy fats", "Protein-rich", "Keeps you full"]
      },
      {
        name: "Sprouts Chaat",
        ingredients: ["Sprouted moong beans", "Onion", "Tomato", "Coriander", "Lemon juice", "Spices"],
        steps: ["Mix all ingredients in a bowl.", "Toss and serve fresh."],
        why: ["High protein", "Low glycemic index", "Supports weight management"]
      },
      {
        name: "Roasted Makhanas (Fox Nuts)",
        ingredients: ["Makhana (fox nuts)", "Ghee or oil (minimal)", "Spices"],
        steps: ["Roast makhana in ghee/oil with spices until crisp."],
        why: ["Low calorie", "High in minerals", "Good for snacking"]
      },
      {
        name: "Paneer Cubes",
        ingredients: ["Paneer (cubed)", "Spices (optional)", "Lemon juice (optional)"],
        steps: ["Eat as is or toss with spices/lemon."],
        why: ["High protein", "Supports muscle health", "Keeps you full"]
      },
      {
        name: "Vegetable Cutlets (air-fried)",
        ingredients: ["Boiled potatoes", "Mixed vegetables", "Spices", "Bread crumbs (optional)", "Oil (for brushing)"],
        steps: ["Mix all ingredients, shape into cutlets, air-fry or bake until golden."],
        why: ["Air-fried, not deep fried", "Vegetables add fiber", "Lower calorie snack"]
      },
      {
        name: "Masala Corn",
        ingredients: ["Boiled corn", "Lemon juice", "Chaat masala", "Spices"],
        steps: ["Mix all ingredients and serve warm."],
        why: ["Vitamins and minerals", "Low fat", "Filling snack"]
      },
      {
        name: "Peanut Chaat",
        ingredients: ["Boiled peanuts", "Onion", "Tomato", "Coriander", "Lemon juice", "Spices"],
        steps: ["Mix all ingredients in a bowl.", "Toss and serve."] ,
        why: ["Protein-rich", "Healthy fats", "Supports blood sugar control"]
      },
      {
        name: "Yogurt with seeds",
        ingredients: ["Yogurt", "Chia seeds", "Flax seeds", "Pumpkin seeds", "Honey (optional)", "Fruit (optional)"],
        steps: ["Mix yogurt with seeds and fruit/honey if desired."],
        why: ["High protein", "Healthy fats", "Supports hormone balance"]
      },
      {
        name: "Boiled Chickpeas",
        ingredients: ["Boiled chickpeas", "Salt", "Spices", "Lemon juice (optional)"],
        steps: ["Eat as is or toss with salt/spices/lemon."],
        why: ["High fiber", "Protein-rich", "Keeps you full"]
      },
      {
        name: "Vegetable Soup",
        ingredients: ["Mixed vegetables", "Water", "Salt", "Pepper", "Spices"],
        steps: ["Boil vegetables with water and spices until soft.", "Blend if desired.", "Serve hot."],
        why: ["Low calorie", "High fiber", "Supports hydration"]
      },
      {
        name: "Lentil Soup",
        ingredients: ["Lentils", "Onion", "Tomato", "Spices", "Water", "Salt"],
        steps: ["Cook lentils with onion, tomato, spices, and water until soft.", "Blend if desired.", "Serve hot."],
        why: ["Plant protein", "Filling snack", "Supports gut health"]
      },
    ],
  },
  {
    name: "Pakistani Breakfasts",
    recipes: [
      {
        name: "Besan Chilla",
        ingredients: ["1 cup besan (gram flour)", "½ chopped onion", "½ chopped tomato", "½ chopped spinach", "1 green chili (optional)", "Salt", "Turmeric", "Water", "Olive oil (small amount)"],
        steps: ["Mix besan with water to form batter.", "Add vegetables and spices.", "Heat pan with little oil.", "Pour batter like pancake.", "Cook both sides till golden."],
        why: ["High protein → reduces insulin spikes", "Low glycemic index → keeps blood sugar stable", "Fiber from veggies → improves gut health", "Helps reduce cravings later in the day"]
      },
      {
        name: "Anda Paratha (multigrain)",
        ingredients: ["2 eggs", "1 multigrain paratha (homemade or store-bought)", "Salt", "Pepper", "Oil (minimal)", "Chopped coriander (optional)"],
        steps: ["Beat eggs with salt, pepper, and coriander.", "Cook paratha lightly on pan.", "Pour eggs over paratha, cook both sides with minimal oil.", "Serve hot."],
        why: ["Protein + fiber combo", "Keeps you full longer", "Supports muscle and hormone health"]
      },
      {
        name: "Vegetable Omelette",
        ingredients: ["2 eggs", "Chopped onion", "Chopped tomato", "Chopped spinach", "Salt", "Pepper", "Oil (minimal)"],
        steps: ["Beat eggs with salt and pepper.", "Add chopped veggies.", "Cook in pan with minimal oil.", "Serve hot."],
        why: ["Protein-rich", "Vegetables add fiber", "Supports hormone balance"]
      },
      {
        name: "Paneer Paratha",
        ingredients: ["1 cup whole wheat flour", "Paneer (grated)", "Chopped coriander", "Salt", "Spices", "Water", "Oil (minimal)"],
        steps: ["Make dough with flour, water, salt.", "Stuff with paneer, coriander, spices.", "Roll and cook on pan with minimal oil."],
        why: ["High protein", "More filling than plain paratha", "Supports muscle health"]
      },
      {
        name: "Yogurt with chia seeds",
        ingredients: ["Yogurt", "Chia seeds", "Fruit (optional)", "Honey (optional)"],
        steps: ["Mix yogurt with chia seeds and fruit/honey if desired."],
        why: ["High protein", "Healthy fats", "Supports hormone balance"]
      },
      {
        name: "Boiled Eggs with vegetables",
        ingredients: ["2 boiled eggs", "Chopped cucumber", "Chopped tomato", "Chopped spinach", "Salt", "Pepper"],
        steps: ["Boil eggs, peel and slice.", "Serve with chopped veggies, salt, and pepper."],
        why: ["Protein-rich", "Vegetables add fiber", "Keeps you full"]
      },
      {
        name: "Oats Porridge",
        ingredients: ["½ cup rolled oats", "1 cup milk (unsweetened)", "Cinnamon", "Chia seeds", "Flax seeds", "Apple slices"],
        steps: ["Cook oats in milk.", "Add cinnamon.", "Top with seeds and apple."],
        why: ["High fiber → improves insulin resistance", "Cinnamon → helps regulate blood sugar", "Seeds → support hormone balance", "Helps reduce belly fat accumulation"]
      },
      {
        name: "Daliya",
        ingredients: ["Broken wheat (dalia)", "Vegetables", "Salt", "Spices"],
        steps: ["Cook dalia and veggies with water, salt, and spices until soft."],
        why: ["High fiber", "Keeps you full longer", "Supports gut health"]
      },
      {
        name: "Anda Bhurji",
        ingredients: ["2 eggs", "Onion", "Tomato", "Green chili (optional)", "Salt", "Pepper", "Oil (minimal)"],
        steps: ["Beat eggs with salt and pepper.", "Sauté onion, tomato, chili in oil.", "Add eggs, scramble, cook through."],
        why: ["Protein-rich", "Vegetables add fiber", "Keeps you full longer"]
      },
      {
        name: "Sprouted Lentil Salad",
        ingredients: ["Sprouted lentils", "Onion", "Tomato", "Cucumber", "Lemon juice", "Salt", "Pepper"],
        steps: ["Mix all ingredients in a bowl.", "Serve fresh."],
        why: ["High protein", "Improves insulin sensitivity", "Very filling and light"]
      },
      {
        name: "Millet Roti with yogurt",
        ingredients: ["Millet flour", "Water", "Salt", "Yogurt"],
        steps: ["Make dough with millet flour, water, salt.", "Roll into rotis, cook on pan.", "Serve with yogurt."],
        why: ["Low glycemic index", "Supports blood sugar control", "Yogurt adds protein"]
      },
      {
        name: "Chickpea Breakfast Bowl",
        ingredients: ["Boiled chickpeas", "Chopped veggies (onion, tomato, cucumber)", "Coriander", "Lemon juice", "Spices"],
        steps: ["Mix all ingredients in a bowl.", "Serve fresh."],
        why: ["High fiber", "Protein-rich", "Supports gut health"]
      },
    ],
  },
  {
    name: "Pakistani Lunch/Dinner",
    recipes: [
      {
        name: "Chicken Karahi (low oil version)",
        ingredients: ["Chicken pieces", "Tomato", "Ginger", "Garlic", "Green chili", "Spices", "Oil (minimal)", "Coriander"],
        steps: ["Heat minimal oil, sauté ginger, garlic, and green chili.", "Add chicken, cook until white.", "Add tomatoes and spices, cook until chicken is done and oil separates.", "Garnish with coriander."],
        why: ["Lean protein", "Low oil", "Spices support metabolism"]
      },
      {
        name: "Chicken Handi (light cream version)",
        ingredients: ["Chicken pieces", "Onion", "Tomato", "Ginger", "Garlic", "Spices", "Light cream or yogurt", "Oil (minimal)"],
        steps: ["Heat minimal oil, sauté onion, ginger, garlic.", "Add chicken, cook until white.", "Add tomato and spices, cook until chicken is done.", "Stir in light cream or yogurt at the end."],
        why: ["Lean protein", "Lighter than traditional handi", "Yogurt/cream adds calcium"]
      },
      {
        name: "Chicken Curry",
        ingredients: ["Chicken pieces", "Onion", "Tomato", "Ginger", "Garlic", "Spices", "Oil (minimal)"],
        steps: ["Heat minimal oil, sauté onion, ginger, garlic.", "Add chicken, cook until white.", "Add tomato and spices, cook until chicken is done."],
        why: ["Lean protein", "Low oil", "Supports muscle health"]
      },
      {
        name: "Daal Chawal (brown rice)",
        ingredients: ["Lentils (daal)", "Onion", "Tomato", "Spices", "Brown rice", "Oil (minimal)"],
        steps: ["Cook lentils with onion, tomato, spices.", "Cook brown rice separately.", "Serve lentils over rice."],
        why: ["Plant protein", "High fiber", "Brown rice supports blood sugar control"]
      },
      {
        name: "Chana Masala",
        ingredients: ["Chickpeas", "Onion", "Tomato", "Ginger", "Garlic", "Spices", "Oil (minimal)"],
        steps: ["Soak and cook chickpeas.", "Sauté onion, ginger, garlic, spices in minimal oil.", "Add tomato, cook down, add chickpeas, simmer."],
        why: ["Protein-rich", "High fiber", "Supports hormone balance"]
      },
      {
        name: "Rajma Curry",
        ingredients: ["Kidney beans", "Onion", "Tomato", "Garlic", "Spices", "Oil (minimal)"],
        steps: ["Soak and cook kidney beans.", "Sauté onion, garlic, spices in minimal oil, add tomato.", "Add beans, simmer."],
        why: ["High fiber", "Improves blood sugar control", "Supports gut bacteria"]
      },
      {
        name: "Aloo Methi (minimal potato)",
        ingredients: ["Potato (small amount)", "Fenugreek leaves (methi)", "Onion", "Spices", "Oil (minimal)"],
        steps: ["Heat minimal oil, sauté onion and spices.", "Add potato and methi, cook until done."],
        why: ["Fenugreek supports blood sugar control", "Minimal potato for lower glycemic load"]
      },
      {
        name: "Palak Chicken",
        ingredients: ["Chicken pieces", "Spinach", "Garlic", "Onion", "Spices", "Oil (minimal)"],
        steps: ["Cook spinach and blend.", "Heat minimal oil, sauté onion, garlic, and spices.", "Add chicken, cook until done.", "Add spinach puree, simmer.", "Serve hot."],
        why: ["Iron-rich", "Lean protein", "Spinach reduces inflammation"]
      },
      {
        name: "Bhindi Masala",
        ingredients: ["Okra (bhindi)", "Onion", "Tomato", "Spices", "Oil (minimal)"],
        steps: ["Sauté okra, onion, tomato, spices in minimal oil until cooked."],
        why: ["Low calorie", "High fiber", "Supports gut health"]
      },
      {
        name: "Lauki Curry",
        ingredients: ["Bottle gourd (lauki)", "Onion", "Tomato", "Spices", "Oil (minimal)"],
        steps: ["Cook all ingredients together with minimal oil until soft."],
        why: ["Low calorie", "Helps reduce bloating", "Easy to digest"]
      },
      {
        name: "Mixed Sabzi",
        ingredients: ["Mixed vegetables", "Onion", "Tomato", "Spices", "Oil (minimal)"],
        steps: ["Chop and cook all vegetables with onion, tomato, spices, and minimal oil."],
        why: ["High fiber", "Reduces inflammation", "Supports gut microbiome"]
      },
      {
        name: "Tinda Masala",
        ingredients: ["Tinda (apple gourd)", "Onion", "Tomato", "Spices", "Oil (minimal)"],
        steps: ["Cook tinda with onion, tomato, spices, and minimal oil."],
        why: ["Low calorie", "Supports digestion"]
      },
      {
        name: "Fish Curry",
        ingredients: ["Fish", "Garlic", "Tomato", "Spices", "Oil (minimal)"],
        steps: ["Cook fish in gravy with garlic, tomato, and spices, and minimal oil."],
        why: ["Rich in Omega-3", "Reduces inflammation", "Improves hormone balance"]
      },
      {
        name: "Grilled Chicken",
        ingredients: ["Chicken pieces", "Yogurt", "Spices", "Lemon juice"],
        steps: ["Marinate chicken in yogurt, spices, lemon.", "Grill until cooked through."],
        why: ["Lean protein", "Grilled, not fried", "Yogurt adds probiotics"]
      },
      {
        name: "Seekh Kebabs (grilled)",
        ingredients: ["Minced chicken or beef", "Onion", "Green chili", "Spices", "Coriander", "Lemon juice"],
        steps: ["Mix all ingredients, shape onto skewers.", "Grill until cooked through."],
        why: ["High protein", "Grilled, not fried", "Spices support metabolism"]
      },
      {
        name: "Shami Kebabs (air-fried)",
        ingredients: ["Minced chicken or beef", "Chana dal", "Onion", "Spices", "Egg (for binding)", "Oil (for brushing)"],
        steps: ["Cook meat, dal, onion, and spices together.", "Grind to paste, shape into patties.", "Air-fry or bake with minimal oil."],
        why: ["High protein", "Air-fried, not deep fried", "Good for snacking"]
      },
      {
        name: "Chicken Pulao (brown rice)",
        ingredients: ["Chicken pieces", "Brown rice", "Onion", "Spices", "Yogurt", "Oil (minimal)"],
        steps: ["Cook chicken with onion, spices, and yogurt.", "Add brown rice, cook until done."],
        why: ["More fiber than white rice", "Lean protein", "Keeps you full longer"]
      },
      {
        name: "Vegetable Pulao",
        ingredients: ["Brown rice", "Mixed vegetables", "Spices", "Onion", "Oil (minimal)"],
        steps: ["Cook rice and veggies with onion, spices, and minimal oil."],
        why: ["High fiber", "Vegetables add vitamins and minerals"]
      },
      {
        name: "Chicken & Vegetable Soup",
        ingredients: ["Chicken pieces", "Mixed vegetables", "Water", "Salt", "Pepper", "Spices"],
        steps: ["Boil chicken and vegetables with water, salt, pepper, and spices until cooked.", "Shred chicken if desired, serve hot."],
        why: ["Lean protein", "Hydrating", "Supports immune health"]
      },
      {
        name: "Chicken Yakhni",
        ingredients: ["Chicken pieces (with bone)", "Water", "Ginger", "Garlic", "Salt", "Pepper", "Spices"],
        steps: ["Boil chicken with all ingredients until broth is flavorful.", "Strain and serve hot."],
        why: ["Hydrating", "Rich in minerals", "Supports recovery"]
      },
      {
        name: "Haleem (small portion, protein-rich)",
        ingredients: ["Chicken or beef", "Lentils", "Broken wheat", "Spices", "Onion", "Ginger", "Garlic", "Oil (minimal)"],
        steps: ["Cook meat, lentils, wheat, onion, ginger, garlic, and spices together until soft.", "Blend to paste, cook with minimal oil."],
        why: ["High protein", "Filling meal", "Good for occasional treat"]
      },
      {
        name: "Nihari (occasional)",
        ingredients: ["Beef or mutton", "Onion", "Ginger", "Garlic", "Spices", "Oil (minimal)"],
        steps: ["Cook meat with onion, ginger, garlic, spices, and minimal oil until tender."],
        why: ["Rich in protein", "Occasional treat", "Spices support metabolism"]
      },
    ],
  },
  {
    name: "Pakistani Snacks",
    recipes: [
      {
        name: "Roasted Chickpeas",
        ingredients: ["Chickpeas (boiled)", "Spices", "Oil (minimal)", "Salt"],
        steps: ["Toss boiled chickpeas with spices, salt, and minimal oil.", "Roast in oven or air-fryer until crisp."],
        why: ["High protein", "High fiber", "Good for snacking"]
      },
      {
        name: "Boiled Eggs",
        ingredients: ["Eggs", "Salt", "Pepper"],
        steps: ["Boil eggs, peel and slice.", "Sprinkle with salt and pepper."],
        why: ["Protein-rich", "Keeps you full", "Supports muscle health"]
      },
      {
        name: "Yogurt",
        ingredients: ["Yogurt (plain or Greek)", "Chia seeds (optional)", "Fruit (optional)", "Honey (optional)"],
        steps: ["Eat yogurt as is or mix with seeds, fruit, or honey."] ,
        why: ["High protein", "Probiotics support gut health", "Good for hormone balance"]
      },
      {
        name: "Fruit Chaat (low sugar fruits)",
        ingredients: ["Seasonal low-sugar fruits (apple, berries, guava, etc.)", "Chaat masala", "Lemon juice"],
        steps: ["Chop fruits, toss with masala and lemon juice."] ,
        why: ["Vitamins and antioxidants", "No added sugar", "Supports immune health"]
      },
      {
        name: "Paneer Cubes",
        ingredients: ["Paneer (cubed)", "Spices (optional)", "Lemon juice (optional)"],
        steps: ["Eat as is or toss with spices/lemon."] ,
        why: ["High protein", "Supports muscle health", "Keeps you full"]
      },
      {
        name: "Nuts Mix",
        ingredients: ["Almonds", "Walnuts", "Pistachios", "Cashews (small amount)", "Raisins (optional)", "Salt (optional)"],
        steps: ["Mix all nuts and eat as a snack."] ,
        why: ["Healthy fats", "Keeps you full", "Supports hormone health"]
      },
      {
        name: "Vegetable Soup",
        ingredients: ["Mixed vegetables", "Water", "Salt", "Pepper", "Spices"],
        steps: ["Boil vegetables with water and spices until soft.", "Blend if desired.", "Serve hot."] ,
        why: ["Low calorie", "High fiber", "Supports hydration"]
      },
      {
        name: "Chicken Soup",
        ingredients: ["Chicken pieces", "Water", "Salt", "Pepper", "Spices", "Vegetables (optional)"],
        steps: ["Boil chicken (and vegetables if using) with water, salt, pepper, and spices until cooked.", "Shred chicken if desired, serve hot."] ,
        why: ["Lean protein", "Hydrating", "Supports immune health"]
      },
      {
        name: "Roasted Peanuts",
        ingredients: ["Peanuts (raw)", "Salt", "Spices (optional)"],
        steps: ["Roast peanuts in oven or pan until golden.", "Toss with salt and spices if desired."] ,
        why: ["Healthy fats", "Protein-rich", "Good for snacking"]
      },
    ],
  },
  {
    name: "American Breakfasts",
    recipes: [
      {
        name: "Greek Yogurt Bowl",
        ingredients: ["Greek yogurt", "Berries", "Chia seeds", "Walnuts", "Honey (optional)", "Granola (optional)"] ,
        steps: ["Mix yogurt with berries, chia seeds, walnuts, and honey/granola if desired."] ,
        why: ["High protein", "Berries are anti-inflammatory", "Healthy fats reduce cravings"]
      },
      {
        name: "Scrambled Eggs with Vegetables",
        ingredients: ["Eggs", "Spinach", "Tomato", "Onion", "Bell pepper", "Salt", "Pepper", "Oil (minimal)"] ,
        steps: ["Beat eggs with salt and pepper.", "Sauté vegetables in minimal oil.", "Add eggs, scramble, cook through."] ,
        why: ["Protein-rich", "Vegetables add fiber", "Keeps you full"]
      },
      {
        name: "Avocado Toast",
        ingredients: ["Whole grain bread", "Avocado", "Lemon juice", "Salt", "Pepper", "Egg (optional)"] ,
        steps: ["Toast bread.", "Mash avocado with lemon, salt, pepper.", "Spread on toast, top with egg if desired."] ,
        why: ["Healthy fats", "Fiber-rich", "Keeps blood sugar stable"]
      },
      {
        name: "Protein Smoothie",
        ingredients: ["Unsweetened milk or plant milk", "Protein powder", "Spinach", "Berries", "Chia seeds"] ,
        steps: ["Blend all ingredients until smooth."] ,
        why: ["High protein", "Antioxidants from berries", "Easy for busy mornings"]
      },
      {
        name: "Chia Seed Pudding",
        ingredients: ["Chia seeds", "Unsweetened milk or plant milk", "Honey (optional)", "Berries"] ,
        steps: ["Mix chia seeds with milk and honey.", "Let sit overnight.", "Top with berries."] ,
        why: ["High fiber", "Healthy fats", "Supports hormone balance"]
      },
      {
        name: "Overnight Oats",
        ingredients: ["Rolled oats", "Unsweetened milk or plant milk", "Chia seeds", "Berries", "Nuts", "Honey (optional)"] ,
        steps: ["Mix oats, milk, chia seeds, and honey.", "Let sit overnight.", "Top with berries and nuts."] ,
        why: ["High fiber", "Slow energy release", "Keeps you full"]
      },
      {
        name: "Cottage Cheese Bowl",
        ingredients: ["Cottage cheese", "Berries", "Nuts", "Seeds", "Honey (optional)"] ,
        steps: ["Mix cottage cheese with berries, nuts, seeds, and honey if desired."] ,
        why: ["High protein", "Calcium-rich", "Supports muscle health"]
      },
      {
        name: "Egg Muffins",
        ingredients: ["Eggs", "Spinach", "Tomato", "Bell pepper", "Salt", "Pepper", "Cheese (optional)"] ,
        steps: ["Beat eggs with salt and pepper.", "Mix in chopped veggies and cheese.", "Pour into muffin tins, bake until set."] ,
        why: ["Protein-rich", "Vegetables add fiber", "Good for meal prep"]
      },
      {
        name: "Spinach Omelette",
        ingredients: ["Eggs", "Spinach", "Onion", "Salt", "Pepper", "Oil (minimal)"] ,
        steps: ["Beat eggs with salt and pepper.", "Add spinach and onion.", "Cook in pan with minimal oil."] ,
        why: ["High protein", "Iron-rich", "Supports hormone balance"]
      },
      {
        name: "Peanut Butter Oatmeal",
        ingredients: ["Rolled oats", "Unsweetened milk", "Peanut butter", "Banana (optional)", "Cinnamon"] ,
        steps: ["Cook oats in milk.", "Stir in peanut butter and cinnamon.", "Top with banana if desired."] ,
        why: ["High fiber", "Healthy fats", "Keeps you full"]
      },
      {
        name: "Protein Pancakes",
        ingredients: ["Oats (blended)", "Eggs", "Banana", "Protein powder", "Baking powder", "Milk"] ,
        steps: ["Blend all ingredients to make batter.", "Cook on nonstick pan until golden."] ,
        why: ["High protein", "Lower glycemic than regular pancakes", "Good for muscle health"]
      },
      {
        name: "Almond Flour Pancakes",
        ingredients: ["Almond flour", "Eggs", "Baking powder", "Milk", "Honey (optional)"] ,
        steps: ["Mix all ingredients to make batter.", "Cook on nonstick pan until golden."] ,
        why: ["Low carb", "Healthy fats", "Supports blood sugar control"]
      },
      {
        name: "Turkey Bacon & Eggs",
        ingredients: ["Turkey bacon", "Eggs", "Salt", "Pepper", "Oil (minimal)"] ,
        steps: ["Cook turkey bacon in pan.", "Scramble or fry eggs with salt and pepper."] ,
        why: ["Lean protein", "Lower fat than pork bacon", "Keeps you full"]
      },
      {
        name: "Breakfast Burrito (whole wheat)",
        ingredients: ["Whole wheat tortilla", "Eggs", "Spinach", "Tomato", "Beans", "Cheese (optional)", "Salsa"] ,
        steps: ["Scramble eggs with spinach and tomato.", "Fill tortilla with eggs, beans, cheese, salsa.", "Wrap and serve."] ,
        why: ["High protein", "Fiber-rich", "Keeps you full"]
      },
      {
        name: "Egg & Veggie Wrap",
        ingredients: ["Whole wheat wrap", "Eggs", "Spinach", "Tomato", "Bell pepper", "Cheese (optional)"] ,
        steps: ["Scramble eggs with veggies.", "Fill wrap with eggs and cheese.", "Roll and serve."] ,
        why: ["Protein-rich", "Vegetables add fiber", "Good for on-the-go"]
      },
    ],
  },
  {
    name: "American Lunch/Dinner",
    recipes: [
      {
        name: "Grilled Chicken Breast",
        ingredients: ["Chicken breast", "Olive oil", "Lemon juice", "Garlic", "Salt", "Pepper", "Herbs (rosemary, thyme)"] ,
        steps: ["Marinate chicken with oil, lemon, garlic, salt, pepper, and herbs.", "Grill until cooked through."] ,
        why: ["Lean protein", "Low fat", "Supports muscle health"]
      },
      {
        name: "Baked Salmon",
        ingredients: ["Salmon fillet", "Olive oil", "Lemon", "Salt", "Pepper", "Dill or parsley"] ,
        steps: ["Place salmon on baking tray.", "Drizzle with oil, lemon, salt, pepper, and herbs.", "Bake until flaky."] ,
        why: ["Rich in Omega-3", "Reduces inflammation", "Supports hormone balance"]
      },
      {
        name: "Turkey Meatballs",
        ingredients: ["Ground turkey", "Egg", "Breadcrumbs (whole wheat)", "Onion", "Garlic", "Spices", "Parsley"] ,
        steps: ["Mix all ingredients, shape into balls.", "Bake or air-fry until cooked through."] ,
        why: ["Lean protein", "Lower fat than beef", "Good for meal prep"]
      },
      {
        name: "Chicken Salad",
        ingredients: ["Cooked chicken breast", "Lettuce", "Tomato", "Cucumber", "Avocado", "Olive oil", "Lemon juice", "Salt", "Pepper"] ,
        steps: ["Chop all ingredients.", "Toss with oil, lemon, salt, and pepper."] ,
        why: ["High protein", "Healthy fats", "Low carb"]
      },
      {
        name: "Tuna Salad",
        ingredients: ["Canned tuna (in water)", "Lettuce", "Tomato", "Cucumber", "Olive oil", "Lemon juice", "Salt", "Pepper"] ,
        steps: ["Drain tuna.", "Mix with chopped veggies, oil, lemon, salt, and pepper."] ,
        why: ["High protein", "Omega-3 from tuna", "Low calorie"]
      },
      {
        name: "Grilled Shrimp",
        ingredients: ["Shrimp (peeled)", "Olive oil", "Garlic", "Lemon juice", "Salt", "Pepper"] ,
        steps: ["Marinate shrimp with oil, garlic, lemon, salt, and pepper.", "Grill until pink."] ,
        why: ["Lean protein", "Low calorie", "Quick to prepare"]
      },
      {
        name: "Beef Stir Fry",
        ingredients: ["Lean beef strips", "Bell peppers", "Broccoli", "Carrot", "Soy sauce (low sodium)", "Garlic", "Ginger", "Oil (minimal)"] ,
        steps: ["Sauté beef in minimal oil.", "Add veggies, garlic, ginger, and soy sauce.", "Cook until veggies are tender."] ,
        why: ["High protein", "Vegetables add fiber", "Iron-rich"]
      },
      {
        name: "Zucchini Noodles",
        ingredients: ["Zucchini (spiralized)", "Olive oil", "Garlic", "Cherry tomatoes", "Basil", "Salt", "Pepper"] ,
        steps: ["Sauté garlic in oil.", "Add zucchini noodles and tomatoes.", "Cook until just tender.", "Top with basil, salt, and pepper."] ,
        why: ["Low carb", "High fiber", "Supports weight management"]
      },
      {
        name: "Stuffed Bell Peppers",
        ingredients: ["Bell peppers", "Ground turkey or chicken", "Brown rice", "Onion", "Tomato", "Spices", "Cheese (optional)"] ,
        steps: ["Cut tops off peppers, remove seeds.", "Stuff with cooked meat, rice, veggies, and spices.", "Top with cheese if desired, bake until peppers are soft."] ,
        why: ["High protein", "Fiber-rich", "Balanced meal"]
      },
      {
        name: "Chicken & Broccoli",
        ingredients: ["Chicken breast", "Broccoli", "Garlic", "Olive oil", "Salt", "Pepper"] ,
        steps: ["Sauté chicken in oil with garlic.", "Add broccoli, cook until tender.", "Season with salt and pepper."] ,
        why: ["Lean protein", "High fiber", "Supports muscle health"]
      },
      {
        name: "Roasted Vegetables",
        ingredients: ["Carrot", "Broccoli", "Cauliflower", "Bell pepper", "Olive oil", "Salt", "Pepper", "Herbs"] ,
        steps: ["Chop veggies, toss with oil, salt, pepper, and herbs.", "Roast until golden."] ,
        why: ["High fiber", "Vitamins and minerals", "Low calorie"]
      },
      {
        name: "Chicken & Quinoa Bowl",
        ingredients: ["Cooked chicken breast", "Quinoa", "Spinach", "Tomato", "Cucumber", "Olive oil", "Lemon juice"] ,
        steps: ["Cook quinoa.", "Top with chicken and veggies.", "Drizzle with oil and lemon."] ,
        why: ["High protein", "Quinoa is a complete protein", "Balanced meal"]
      },
      {
        name: "Turkey Chili",
        ingredients: ["Ground turkey", "Kidney beans", "Tomato", "Onion", "Bell pepper", "Spices", "Oil (minimal)"] ,
       
        why: ["High protein", "High fiber", "Filling meal"]
      },
      {
        name: "Lentil Soup",
        ingredients: ["Lentils", "Carrot", "Celery", "Onion", "Garlic", "Spices", "Water", "Salt", "Pepper"] ,
        steps: ["Cook lentils and veggies with water and spices until soft.", "Blend if desired, serve hot."] ,
        why: ["Plant protein", "High fiber", "Supports gut health"]
      },
      {
        name: "Chicken Fajita Bowl",
        ingredients: ["Chicken breast", "Bell peppers", "Onion", "Brown rice", "Spices", "Olive oil"] ,
        steps: ["Sauté chicken, peppers, and onion with spices in oil.", "Serve over brown rice."] ,
        why: ["High protein", "Fiber-rich", "Balanced meal"]
      },
      {
        name: "Steak with vegetables",
        ingredients: ["Lean steak", "Broccoli", "Carrot", "Olive oil", "Salt", "Pepper"] ,
        steps: ["Grill or pan-cook steak to desired doneness.", "Sauté or steam veggies, serve with steak."] ,
        why: ["High protein", "Iron-rich", "Supports muscle health"]
      },
      {
        name: "Grilled Tilapia",
        ingredients: ["Tilapia fillet", "Olive oil", "Lemon", "Salt", "Pepper", "Herbs"] ,
        steps: ["Drizzle tilapia with oil, lemon, salt, pepper, and herbs.", "Grill until cooked through."] ,
        why: ["Lean protein", "Low calorie", "Quick to prepare"]
      },
      {
        name: "Baked Cod",
        ingredients: ["Cod fillet", "Olive oil", "Lemon", "Salt", "Pepper", "Herbs"] ,
        steps: ["Place cod on baking tray.", "Drizzle with oil, lemon, salt, pepper, and herbs.", "Bake until flaky."] ,
        why: ["Lean protein", "Low calorie", "Supports muscle health"]
      },
      {
        name: "Chicken Lettuce Wraps",
        ingredients: ["Ground chicken", "Lettuce leaves", "Carrot", "Cucumber", "Soy sauce (low sodium)", "Ginger", "Garlic"] ,
        steps: ["Cook chicken with ginger, garlic, and soy sauce.", "Spoon into lettuce leaves, top with carrot and cucumber."] ,
        why: ["Low carb", "High protein", "Crunchy and filling"]
      },
    ],
  },
  {
    name: "American Snacks",
    recipes: [
      {
        name: "Apple + Peanut Butter",
        ingredients: ["Apple slices", "Peanut butter (unsweetened)"] ,
        steps: ["Slice apple.", "Spread peanut butter on slices."] ,
        why: ["Fiber + healthy fat", "Keeps you full", "No added sugar"]
      },
      {
        name: "Greek Yogurt",
        ingredients: ["Greek yogurt", "Berries (optional)", "Honey (optional)"] ,
        steps: ["Eat yogurt as is or top with berries/honey."] ,
        why: ["High protein", "Probiotics support gut health", "Good for hormone balance"]
      },
      {
        name: "Cottage Cheese",
        ingredients: ["Cottage cheese", "Pineapple or berries (optional)"] ,
        steps: ["Eat cottage cheese as is or top with fruit."] ,
        why: ["High protein", "Calcium-rich", "Keeps you full"]
      },
      {
        name: "Protein Bars",
        ingredients: ["Protein bar (low sugar, high protein)"] ,
        steps: ["Eat as a snack on the go."] ,
        why: ["High protein", "Convenient", "Supports muscle health"]
      },
      {
        name: "Almonds",
        ingredients: ["Almonds (raw or roasted)"] ,
        steps: ["Eat a small handful as a snack."] ,
        why: ["Healthy fats", "Keeps you full", "Supports hormone health"]
      },
      {
        name: "Walnuts",
        ingredients: ["Walnuts (raw or roasted)"] ,
        steps: ["Eat a small handful as a snack."] ,
        why: ["Omega-3 fats", "Supports brain health", "Reduces inflammation"]
      },
      {
        name: "Pumpkin Seeds",
        ingredients: ["Pumpkin seeds (raw or roasted)"] ,
        steps: ["Eat a small handful as a snack."] ,
        why: ["High in magnesium", "Supports hormone balance", "Keeps you full"]
      },
      {
        name: "Trail Mix",
        ingredients: ["Almonds", "Walnuts", "Pumpkin seeds", "Raisins (small amount)", "Dark chocolate chips (optional)"] ,
        steps: ["Mix all ingredients and eat as a snack."] ,
        why: ["Healthy fats", "Antioxidants", "Keeps you full"]
      },
      {
        name: "Hard-Boiled Eggs",
        ingredients: ["Eggs", "Salt", "Pepper"] ,
        steps: ["Boil eggs, peel and slice.", "Sprinkle with salt and pepper."] ,
        why: ["Protein-rich", "Keeps you full", "Supports muscle health"]
      },
      {
        name: "Protein Shake",
        ingredients: ["Protein powder", "Unsweetened milk or plant milk", "Berries (optional)"] ,
        steps: ["Blend all ingredients until smooth."] ,
        why: ["High protein", "Quick and convenient", "Good for post-workout"]
      },
      {
        name: "Celery + Hummus",
        ingredients: ["Celery sticks", "Hummus (chickpeas, tahini, lemon, garlic)"] ,
        steps: ["Dip celery sticks in hummus and enjoy."] ,
        why: ["Low calorie", "High fiber", "Keeps you full"]
      },
      {
        name: "Carrots + Hummus",
        ingredients: ["Carrot sticks", "Hummus (chickpeas, tahini, lemon, garlic)"] ,
        steps: ["Dip carrot sticks in hummus and enjoy."] ,
        why: ["Low calorie", "High fiber", "Supports gut health"]
      },
    ],
  },
  {
    name: "Soups",
    recipes: [
      {
        name: "Lentil Vegetable Soup",
        ingredients: ["Lentils", "Carrot", "Celery", "Onion", "Tomato", "Garlic", "Spices", "Water", "Salt", "Pepper"],
        steps: ["Cook lentils and veggies with water and spices until soft.", "Blend if desired, serve hot."],
        why: ["Plant protein", "High fiber", "Supports gut health"]
      },
      {
        name: "Chicken & Spinach Soup",
        ingredients: ["Chicken breast", "Spinach", "Carrot", "Onion", "Garlic", "Water", "Salt", "Pepper"],
        steps: ["Boil chicken with veggies, garlic, salt, and pepper until cooked.", "Shred chicken, add spinach, simmer, serve hot."],
        why: ["Lean protein", "Iron-rich", "Hydrating"]
      },
      {
        name: "Tomato Basil Soup",
        ingredients: ["Tomatoes", "Onion", "Garlic", "Basil", "Olive oil", "Salt", "Pepper"],
        steps: ["Sauté onion and garlic in oil.", "Add tomatoes, cook until soft.", "Blend with basil, season, serve hot."],
        why: ["Antioxidants", "Low calorie", "Supports immune health"]
      },
    ],
  },
  {
    name: "Salads",
    recipes: [
      {
        name: "Chickpea Salad",
        ingredients: ["Boiled chickpeas", "Cucumber", "Tomato", "Onion", "Coriander", "Lemon juice", "Salt", "Pepper"],
        steps: ["Mix all ingredients in a bowl, toss and serve."] ,
        why: ["High fiber", "Protein-rich", "Supports gut health"]
      },
      {
        name: "Quinoa Veggie Salad",
        ingredients: ["Cooked quinoa", "Bell peppers", "Cucumber", "Tomato", "Spinach", "Olive oil", "Lemon juice", "Salt", "Pepper"],
        steps: ["Mix all ingredients in a bowl, toss and serve."] ,
        why: ["Complete protein", "High fiber", "Keeps you full"]
      },
      {
        name: "Greek Salad",
        ingredients: ["Cucumber", "Tomato", "Red onion", "Feta cheese", "Olives", "Olive oil", "Lemon juice", "Oregano", "Salt", "Pepper"],
        steps: ["Chop veggies, mix with cheese and olives.", "Toss with oil, lemon, and seasonings."] ,
        why: ["Healthy fats", "Vitamins and minerals", "Low carb"]
      },
    ],
  },
  {
    name: "Desserts",
    recipes: [
      {
        name: "Chia Seed Pudding",
        ingredients: ["Chia seeds", "Unsweetened milk or plant milk", "Honey (optional)", "Berries"],
        steps: ["Mix chia seeds with milk and honey.", "Let sit overnight.", "Top with berries."] ,
        why: ["High fiber", "Healthy fats", "Supports hormone balance"]
      },
      {
        name: "Fruit & Nut Yogurt Parfait",
        ingredients: ["Greek yogurt", "Berries", "Nuts", "Honey (optional)"] ,
        steps: ["Layer yogurt, berries, and nuts in a glass.", "Drizzle with honey if desired."] ,
        why: ["High protein", "Antioxidants", "No added sugar"]
      },
      {
        name: "Baked Apple with Cinnamon",
        ingredients: ["Apple", "Cinnamon", "Walnuts", "Honey (optional)"] ,
        steps: ["Core apple, fill with walnuts and cinnamon.", "Bake until soft, drizzle with honey if desired."] ,
        why: ["Natural sweetness", "Fiber-rich", "No refined sugar"]
      },
    ],
  },
  {
    name: "Drinks",
    recipes: [
      {
        name: "Spearmint Tea",
        ingredients: ["Spearmint leaves", "Water", "Honey (optional)"] ,
        steps: ["Boil water, add spearmint leaves, steep, strain, and serve.", "Add honey if desired."] ,
        why: ["May help reduce androgen levels", "Calming", "No caffeine"]
      },
      {
        name: "Golden Milk (Turmeric Latte)",
        ingredients: ["Unsweetened milk or plant milk", "Turmeric", "Black pepper", "Ginger", "Honey (optional)"] ,
        steps: ["Heat milk with turmeric, pepper, and ginger.", "Sweeten with honey if desired."] ,
        why: ["Anti-inflammatory", "Supports hormone balance", "Comforting"]
      },
      {
        name: "Berry Smoothie",
        ingredients: ["Berries", "Unsweetened milk or plant milk", "Chia seeds", "Honey (optional)"] ,
        steps: ["Blend all ingredients until smooth."] ,
        why: ["Antioxidants", "High fiber", "No added sugar"]
      },
    ],
  },
];

// --- Healthy Tips Section ---
const HEALTHY_TIPS_CATEGORIES = [
  {
    name: "Hydration",
    tips: [
      {
        title: "Drink water before every meal",
        details: "Helps reduce overeating and supports metabolism.",
        why: "Staying hydrated can help reduce cravings and support hormone balance."
      },
      {
        title: "Keep water nearby and sip through the day",
        details: "Carry a water bottle and set reminders if needed.",
        why: "Consistent hydration supports energy and skin health."
      },
    ]
  },
  {
    name: "Sleep",
    tips: [
      {
        title: "Aim for 7-9 hours of sleep nightly",
        details: "Try to keep a consistent bedtime and wind-down routine.",
        why: "Good sleep helps regulate hormones and reduce stress."
      },
      {
        title: "Limit screen time before bed",
        details: "Avoid phones and laptops at least 30 minutes before sleep.",
        why: "Blue light can disrupt melatonin and sleep quality."
      },
    ]
  },
  {
    name: "Nutrition",
    tips: [
      {
        title: "Add a veggie to every plate",
        details: "Try to include vegetables at every meal for fiber and nutrients.",
        why: "Fiber helps with blood sugar control and gut health."
      },
      {
        title: "Eat slowly and mindfully",
        details: "Put down your fork between bites and savor your food.",
        why: "Mindful eating can help with digestion and portion control."
      },
      {
        title: "Try a new fruit or veggie each week",
        details: "Explore seasonal produce for variety and nutrients.",
        why: "Diversity in diet supports gut microbiome and overall health."
      },
      {
        title: "Keep healthy snacks visible",
        details: "Place fruits, nuts, or yogurt at eye level in the fridge.",
        why: "Visual cues make healthy choices easier."
      },
    ]
  },
  {
    name: "Movement",
    tips: [
      {
        title: "Take a 5-minute walk after meals",
        details: "A short walk can help with digestion and blood sugar.",
        why: "Movement after eating helps lower blood sugar spikes."
      },
      {
        title: "Batch-cook healthy meals on weekends",
        details: "Prepare meals in advance to make healthy eating easier.",
        why: "Meal prep reduces stress and helps you stick to your goals."
      },
    ]
  },
  {
    name: "Stress & Mindset",
    tips: [
      {
        title: "Practice deep breathing for stress",
        details: "Try 4-7-8 breathing or box breathing for a quick reset.",
        why: "Deep breathing calms the nervous system and reduces cortisol."
      },
      {
        title: "Celebrate small wins!",
        details: "Acknowledge your progress, no matter how tiny.",
        why: "Positive reinforcement builds motivation and consistency."
      },
    ]
  },
  {
    name: "Swaps",
    tips: [
      {
        title: "Swap sugary drinks for herbal tea",
        details: "Try mint, chamomile, or spearmint tea instead of soda or juice.",
        why: "Herbal teas hydrate and may help with hormone balance."
      },
    ]
  },
];

const affirmations = [
  "Laiba is not fighting alone. Afshaan is in the party.",
  "Tiny wins count. Today still matters.",
  "Consistency beats perfection every single time.",
  "A gentle routine is stronger than panic.",
  "PCOS picked the wrong team.",
];

function classNames(...parts) {
  return parts.filter(Boolean).join(" ");
}

function getDailyIndex(seedOffset = 0) {
  const today = new Date();
  const seed = today.getFullYear() * 1000 + (today.getMonth() + 1) * 100 + today.getDate() + seedOffset;
  return Math.abs(seed);
}

function useLocalStorage(key, initialValue) {
  const [value, setValue] = useState(() => {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : initialValue;
    } catch {
      return initialValue;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // ignore write failures
    }
  }, [key, value]);

  return [value, setValue];
}

// StatCard and ChecklistItem are defined in UIComponents

export default function LaibaPCOSLoveGuideApp() {
  const { user, profile, partner } = useAuth();
  
  const name = profile?.name || "Laiba";
  const [started, setStarted] = useLocalStorage("pcos-love-started", false);
  const [completed, setCompleted] = useLocalStorage("pcos-love-completed", {});
  const [todayWater, setTodayWater] = useState(0);
  const [waterGoal, setWaterGoal] = useLocalStorage("pcos-love-water-goal", 8);
  
  // Load water from database on mount
  useEffect(() => {
    if (user?.id) {
      getWaterIntake(user.id).then(data => {
        if (data) setTodayWater(data.cups || 0);
      }).catch(err => console.error('Error loading water:', err));
    }
  }, [user?.id]);
  
  // Save water to database when changed
  const updateWater = async (newCups) => {
    setTodayWater(newCups);
    if (user?.id) {
      try {
        await saveWaterIntake(user.id, newCups);
      } catch (err) {
        console.error('Error saving water:', err);
      }
    }
  };
  const [, setPartnerNotes] = useState([]);
  const [showCompanion, setShowCompanion] = useState(false);
  const [showMoodCheck, setShowMoodCheck] = useState(false);
  const [showDoctorPrep, setShowDoctorPrep] = useState(false);
  
  const [activeSection, setActiveSection] = useState("main");
  const [categoryIdx, setCategoryIdx] = useState(0);
  const [openRecipe, setOpenRecipe] = useState(null);
  const [tipCategoryIdx, setTipCategoryIdx] = useState(0);
  const [openTip, setOpenTip] = useState(null);
  const [exerciseCategory, setExerciseCategory] = useState("all");
  const [openExercise, setOpenExercise] = useState(null);

  const daySeed = getDailyIndex();
  const dateKey = new Date().toISOString().split('T')[0];

  // Fetch partner notes on mount
  useEffect(() => {
    if (user?.id && partner?.id) {
      getNotes(user.id).then(setPartnerNotes).catch(console.error);
    }
  }, [user?.id, partner?.id]);

  // Period tracking state
  const [periodHistory, setPeriodHistory] = useState([]);
  
  useEffect(() => {
    if (user?.id) {
      supabase
        .from('period_tracking')
        .select('*')
        .eq('user_id', user.id)
        .order('start_date', { ascending: false })
        .limit(5)
        .then(({ data }) => setPeriodHistory(data || []));
    }
  }, [user?.id]);

  const breakfast = BREAKFASTS[daySeed % BREAKFASTS.length];
  const exercise = EXERCISES[daySeed % EXERCISES.length];
  const affirmation = affirmations[daySeed % affirmations.length];

  const todayCompleted = completed[dateKey] || {
    breakfast: false,
    movement: false,
    water: false,
    supplement: false,
    mindfulness: false
  };

  const handleComplete = (key) => {
    setCompleted(prev => ({
      ...prev,
      [dateKey]: {
        ...(prev[dateKey] || {}),
        [key]: !prev[dateKey]?.[key]
      }
    }));
  };

  const toggleTask = (key) => {
    setCompleted((prev) => {
      const updated = {
        ...prev,
        [dateKey]: {
          ...todayCompleted,
          [key]: !todayCompleted[key],
        },
      };
      saveProgress(user.id, dateKey, updated[dateKey]);
      return updated;
    });
  };

  // completionCount tracked in QuestCard components

  const confettiHearts = Array.from({ length: 12 }).map((_, i) => (
    <motion.div
      key={i}
      initial={{ opacity: 0, y: 10, scale: 0.5 }}
      animate={{ opacity: 1, y: [0, -10, 0], scale: 1 }}
      transition={{ delay: i * 0.06, duration: 2.2, repeat: Infinity, repeatType: "mirror" }}
      className="absolute text-rose-300"
      style={{ left: `${8 + i * 7}%`, top: `${10 + (i % 3) * 14}%` }}
    >
      <Heart className="h-4 w-4 fill-current" />
    </motion.div>
  ));

  // --- Section Navigation ---
  // Always render navigation bar for landing page
  if (!started) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50 text-slate-800">
        <AnimatePresence mode="wait">
          <motion.div
            key="intro"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            className="min-h-screen flex items-center justify-center p-6"
          >
            <div className="absolute top-6 left-6">
              <button onClick={() => setActiveSection("main")}
                className="rounded-full bg-white/90 px-4 py-2 text-rose-600 font-semibold border border-rose-200 shadow hover:bg-rose-50 transition">
                ← Home
              </button>
            </div>
            <div className="relative w-full max-w-4xl overflow-hidden rounded-[2rem] bg-white/85 backdrop-blur-xl shadow-2xl border border-rose-100">
              {confettiHearts}
              <div className="grid md:grid-cols-2 gap-0">
                <div className="p-8 md:p-10 bg-gradient-to-br from-rose-100 via-pink-100 to-orange-100">
                  <div className="inline-flex items-center gap-2 rounded-full bg-white/80 px-4 py-2 text-sm font-medium text-rose-700 shadow-sm">
                    <Sparkles className="h-4 w-4" />
                    Cute Quest Mode Activated
                  </div>
                  <h1 className="mt-6 text-4xl md:text-5xl font-black leading-tight text-slate-800">
                    Laiba, are you ready to fight PCOS with Afshaan? 💖
                  </h1>
                  <div className="mt-8 space-y-3">
                    <div className="flex items-center gap-3 rounded-2xl bg-white/80 p-4 shadow-sm">
                      <Shield className="h-5 w-5 text-rose-500" />
                      <span>Daily PCOS support plan with minimal effort</span>
                    </div>
                    <div className="flex items-center gap-3 rounded-2xl bg-white/80 p-4 shadow-sm">
                      <BookOpen className="h-5 w-5 text-rose-500" />
                      <span>Friendly guidebook</span>
                    </div>
                    <div className="flex items-center gap-3 rounded-2xl bg-white/80 p-4 shadow-sm">
                      <Heart className="h-5 w-5 text-rose-500" />
                      <span>Built with love by Afshaan for Laiba</span>
                    </div>
                  </div>
                </div>
                <div className="p-8 md:p-10 flex flex-col justify-center">
                  <div className="rounded-3xl border border-rose-100 bg-gradient-to-b from-white to-rose-50 p-6 shadow-sm">
                    <div className="text-sm uppercase tracking-[0.2em] text-rose-500 font-semibold">
                      Player Setup
                    </div>
                    <label className="block mt-5 text-sm text-slate-600">Welcome, {name}!</label>
                    <p className="mt-3 text-sm text-slate-500 leading-6">
                      The big goal: support better routines, steadier energy, healthier habits, and future fertility planning with kindness.
                    </p>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setStarted(true)}
                      className="mt-6 w-full rounded-2xl bg-gradient-to-r from-rose-500 via-pink-500 to-orange-400 px-5 py-4 text-white font-semibold shadow-lg"
                    >
                      Yes, let’s fight PCOS together ✨
                    </motion.button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }

  // --- Cook Book Section ---
  if (activeSection === "cookbook") {
    const category = COOKBOOK_CATEGORIES[categoryIdx];
    const showModal = openRecipe !== null;
    let modalRecipe = null;
    if (showModal) {
      const { categoryIdx: catIdx, recipeIdx } = openRecipe;
      const cat = COOKBOOK_CATEGORIES[catIdx];
      modalRecipe = cat && cat.recipes[recipeIdx];
    }
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50 text-slate-800">
        <div className="max-w-5xl mx-auto py-10 px-4">
          <div className="flex items-center justify-between mb-8 flex-wrap gap-2">
            <button onClick={() => setActiveSection("main")}
              className="text-rose-600 hover:underline font-semibold">← Home</button>
            <h2 className="text-3xl font-black flex items-center gap-2"><CookingPot className="h-7 w-7 text-orange-500" />Cook Book</h2>
            <div className="flex gap-2 flex-wrap">
              {COOKBOOK_CATEGORIES.map((cat, idx) => (
                <button key={cat.name} onClick={() => setCategoryIdx(idx)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold border transition ${categoryIdx === idx ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-slate-700 border-rose-100 hover:border-rose-200'}`}>{cat.name}</button>
              ))}
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {category.recipes.map((r, idx) => (
              <button key={r.name} onClick={() => setOpenRecipe({ categoryIdx, recipeIdx: idx })}
                className="rounded-3xl border border-rose-100 bg-white/90 p-6 shadow-xl flex flex-col hover:shadow-2xl hover:border-orange-300 transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-400">
                <div className="font-bold text-lg mb-1">{r.name}</div>
                <div className="text-xs text-slate-400 mt-1">Click for details</div>
              </button>
            ))}
          </div>
        </div>
        {/* Modal for recipe details */}
        {showModal && modalRecipe && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
            <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full relative animate-fadeIn">
              <button onClick={() => setOpenRecipe(null)} className="absolute top-3 right-3 text-rose-500 hover:text-rose-700 text-xl font-bold">×</button>
              <h3 className="text-2xl font-black mb-2">{modalRecipe.name}</h3>
              {modalRecipe.ingredients || modalRecipe.steps || modalRecipe.why ? (
                <>
                  {modalRecipe.ingredients && (
                    <div className="mt-4">
                      <div className="font-semibold text-orange-700 mb-1">Ingredients:</div>
                      <ul className="list-disc ml-6 text-slate-700">
                        {modalRecipe.ingredients.map((item, i) => <li key={i}>{item}</li>)}
                      </ul>
                    </div>
                  )}
                  {modalRecipe.steps && (
                    <div className="mt-4">
                      <div className="font-semibold text-orange-700 mb-1">How to Make:</div>
                      <ol className="list-decimal ml-6 text-slate-700">
                        {modalRecipe.steps.map((step, i) => <li key={i}>{step}</li>)}
                      </ol>
                    </div>
                  )}
                  {modalRecipe.why && (
                    <div className="mt-4">
                      <div className="font-semibold text-emerald-700 mb-1">Why It’s Good for PCOS:</div>
                      <ul className="list-disc ml-6 text-slate-700">
                        {modalRecipe.why.map((reason, i) => <li key={i}>{reason}</li>)}
                      </ul>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-slate-500 mt-4">More details coming soon!</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- Healthy Tips Section ---
  if (activeSection === "tips") {
    // --- Browsable, categorized, modal Healthy Tips ---
    const tipCategory = HEALTHY_TIPS_CATEGORIES[tipCategoryIdx];
    const showTipModal = openTip !== null;
    let modalTip = null;
    if (showTipModal) {
      const { categoryIdx, tipIdx } = openTip;
      const cat = HEALTHY_TIPS_CATEGORIES[categoryIdx];
      modalTip = cat && cat.tips[tipIdx];
    }
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50 text-slate-800">
        <div className="max-w-4xl mx-auto py-10 px-4">
          <div className="flex items-center justify-between mb-8 flex-wrap gap-2">
            <button onClick={() => setActiveSection("main")}
              className="text-rose-600 hover:underline font-semibold">← Home</button>
            <h2 className="text-3xl font-black flex items-center gap-2"><Star className="h-7 w-7 text-emerald-500" />Healthy Tips</h2>
            <div className="flex gap-2 flex-wrap">
              {HEALTHY_TIPS_CATEGORIES.map((cat, idx) => (
                <button key={cat.name} onClick={() => setTipCategoryIdx(idx)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold border transition ${tipCategoryIdx === idx ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-slate-700 border-emerald-100 hover:border-emerald-200'}`}>{cat.name}</button>
              ))}
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {tipCategory.tips.map((t, idx) => (
              <button key={t.title} onClick={() => setOpenTip({ categoryIdx: tipCategoryIdx, tipIdx: idx })}
                className="rounded-3xl border border-emerald-100 bg-white/90 p-6 shadow-xl flex flex-col hover:shadow-2xl hover:border-emerald-300 transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-400">
                <div className="font-bold text-lg mb-1">{t.title}</div>
                <div className="text-xs text-slate-400 mt-1">Click for details</div>
              </button>
            ))}
          </div>
        </div>
        {/* Modal for tip details */}
        {showTipModal && modalTip && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
            <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full relative animate-fadeIn">
              <button onClick={() => setOpenTip(null)} className="absolute top-3 right-3 text-emerald-500 hover:text-emerald-700 text-xl font-bold">×</button>
              <h3 className="text-2xl font-black mb-2">{modalTip.title}</h3>
              <div className="mt-4">
                <div className="font-semibold text-emerald-700 mb-1">Details:</div>
                <div className="text-slate-700 mb-2">{modalTip.details}</div>
                <div className="font-semibold text-orange-700 mb-1">Why It’s Good for PCOS:</div>
                <div className="text-slate-700">{modalTip.why}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- Exercise Section ---
  if (activeSection === "exercises") {
    const filteredExercises = exerciseCategory === "all" 
      ? EXERCISES 
      : EXERCISES.filter(e => e.category === exerciseCategory);
    const modalExercise = openExercise !== null ? EXERCISES[openExercise] : null;

    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50 text-slate-800">
        <div className="max-w-5xl mx-auto py-10 px-4">
          <div className="flex items-center justify-between mb-8 flex-wrap gap-2">
            <button onClick={() => setActiveSection("main")}
              className="text-rose-600 hover:underline font-semibold">← Home</button>
            <h2 className="text-3xl font-black flex items-center gap-2">
              <Dumbbell className="h-7 w-7 text-amber-500" />
              Exercise Library
            </h2>
            <div className="flex gap-2 flex-wrap">
              {EXERCISE_CATEGORIES.map((cat) => (
                <button 
                  key={cat.key} 
                  onClick={() => setExerciseCategory(cat.key)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold border transition ${
                    exerciseCategory === cat.key 
                      ? 'bg-amber-500 text-white border-amber-500' 
                      : 'bg-white text-slate-700 border-rose-100 hover:border-rose-200'
                  }`}
                >
                  {cat.icon} {cat.name}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-6 p-4 rounded-2xl bg-amber-50 border border-amber-200">
            <p className="text-sm text-amber-800">
              <strong>PCOS Tip:</strong> Consistency matters more than intensity. Even 10-15 minutes of movement daily can help with insulin sensitivity and mood. Find what you enjoy!
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredExercises.map((ex, idx) => (
              <button 
                key={ex.title} 
                onClick={() => setOpenExercise(EXERCISES.indexOf(ex))}
                className="rounded-3xl border border-rose-100 bg-white/90 p-6 shadow-xl flex flex-col hover:shadow-2xl hover:border-amber-300 transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-400 text-left"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-bold text-lg mb-1">{ex.title}</div>
                    <div className="text-sm text-slate-500">{ex.subtitle}</div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                    ex.difficulty === 'easy' ? 'bg-emerald-100 text-emerald-700' :
                    ex.difficulty === 'medium' ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {ex.difficulty}
                  </span>
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs text-slate-400">
                  <span>⏱️ {ex.duration}</span>
                  <span>•</span>
                  <span className="capitalize">{ex.category}</span>
                </div>
                <p className="mt-3 text-sm text-slate-600 line-clamp-2">{ex.why}</p>
                <div className="mt-auto pt-3 text-xs text-amber-600 font-medium">Click for details →</div>
              </button>
            ))}
          </div>
        </div>

        {/* Exercise Detail Modal */}
        {modalExercise && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
            <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto relative">
              <button 
                onClick={() => setOpenExercise(null)} 
                className="absolute top-3 right-3 text-rose-500 hover:text-rose-700 text-xl font-bold"
              >
                ×
              </button>
              
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-2xl font-black">{modalExercise.title}</h3>
                  <div className="text-slate-500 mt-1">{modalExercise.subtitle}</div>
                </div>
                <span className={`text-xs px-3 py-1 rounded-full font-semibold ${
                  modalExercise.difficulty === 'easy' ? 'bg-emerald-100 text-emerald-700' :
                  modalExercise.difficulty === 'medium' ? 'bg-amber-100 text-amber-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {modalExercise.difficulty}
                </span>
              </div>

              <div className="flex items-center gap-4 mb-4 text-sm text-slate-500">
                <span>⏱️ {modalExercise.duration}</span>
                <span className="capitalize">• {modalExercise.category}</span>
              </div>

              <div className="rounded-2xl bg-rose-50 border border-rose-100 p-4 mb-4">
                <div className="font-semibold text-rose-700 mb-1">Why It's Good for You</div>
                <p className="text-slate-600">{modalExercise.why}</p>
              </div>

              {modalExercise.steps && (
                <div className="mb-4">
                  <div className="font-semibold text-amber-700 mb-2">How to Do It:</div>
                  <ol className="list-decimal ml-6 text-slate-700 space-y-2">
                    {modalExercise.steps.map((step, i) => (
                      <li key={i}>{step}</li>
                    ))}
                  </ol>
                </div>
              )}

              {modalExercise.benefits && (
                <div className="mb-4">
                  <div className="font-semibold text-emerald-700 mb-2">Benefits:</div>
                  <ul className="list-disc ml-6 text-slate-700 space-y-1">
                    {modalExercise.benefits.map((benefit, i) => (
                      <li key={i}>{benefit}</li>
                    ))}
                  </ul>
                </div>
              )}

              {modalExercise.pcosTip && (
                <div className="rounded-2xl bg-violet-50 border border-violet-200 p-4">
                  <div className="font-semibold text-violet-700 mb-1">💡 PCOS-Specific Tip</div>
                  <p className="text-slate-600 text-sm">{modalExercise.pcosTip}</p>
                </div>
              )}

              <button
                onClick={() => {
                  toggleTask("movement");
                  setOpenExercise(null);
                }}
                className="mt-6 w-full rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3 font-semibold hover:from-amber-600 hover:to-orange-600 transition"
              >
                Mark as Done for Today ✓
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- Main Section ---
  if (activeSection === "main") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-purple-50 pb-24 relative overflow-hidden">
        {/* Floating Hearts Animation */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ 
                y: -20, 
                x: Math.random() * 400,
                opacity: 0 
              }}
              animate={{ 
                y: 800,
                opacity: [0, 0.6, 0.3, 0],
                rotate: [0, 360]
              }}
              transition={{
                duration: 15 + Math.random() * 10,
                repeat: Infinity,
                delay: Math.random() * 5,
              }}
              className="absolute text-pink-200 text-xl"
            >
              {["💕", "💖", "💗", "✨", "🌸", "💫"][i % 6]}
            </motion.div>
          ))}
        </div>

        {/* Header */}
        <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-lg border-b border-rose-100">
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
            <div>
              <p className="text-xs text-rose-400">Welcome back,</p>
              <h1 className="text-xl font-bold text-slate-800">{name} 💕</h1>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowCompanion(true)}
                className="w-10 h-10 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 flex items-center justify-center text-white shadow-lg"
              >
                <Sparkles className="w-5 h-5" />
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
          {/* Daily Affirmation Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-pink-500 to-rose-500 rounded-3xl p-6 text-white shadow-xl"
          >
            <p className="text-sm opacity-90 mb-1">Today's Affirmation</p>
            <p className="text-lg font-medium leading-relaxed">{affirmation}</p>
          </motion.div>

          {/* Cycle Info Card - Show if period history exists */}
          {periodHistory && periodHistory.length > 0 && (() => {
            const lastPeriod = periodHistory[0];
            if (!lastPeriod?.start_date) return null;
            
            const lastStartDate = new Date(lastPeriod.start_date);
            const today = new Date();
            const daysSince = Math.floor((today - lastStartDate) / (1000 * 60 * 60 * 24)) + 1;
            const currentDay = daysSince > 0 ? ((daysSince - 1) % 28) + 1 : 1;
            
            let phase, phaseEmoji, daysUntilOvulation, daysUntilPeriod;
            
            if (currentDay <= 5) {
              phase = 'Period';
              phaseEmoji = '🩸';
              daysUntilOvulation = 14 - currentDay;
              daysUntilPeriod = 28 - currentDay;
            } else if (currentDay <= 13) {
              phase = 'Follicular';
              phaseEmoji = '🌸';
              daysUntilOvulation = 14 - currentDay;
              daysUntilPeriod = 28 - currentDay;
            } else if (currentDay <= 16) {
              phase = 'Ovulation';
              phaseEmoji = '✨';
              daysUntilOvulation = 0;
              daysUntilPeriod = 28 - currentDay;
            } else {
              phase = 'Luteal';
              phaseEmoji = '🌙';
              daysUntilOvulation = (28 - currentDay) + 14;
              daysUntilPeriod = 28 - currentDay;
            }
            
            return (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-br from-teal-400 to-cyan-500 rounded-3xl p-6 text-white shadow-xl cursor-pointer"
                onClick={() => setActiveSection("period")}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-90">Cycle Day {currentDay}</p>
                    <p className="text-2xl font-bold mt-1">{phaseEmoji} {phase}</p>
                    {phase === 'Ovulation' && (
                      <p className="text-sm mt-1">💕 Chance to get pregnant</p>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="bg-white/20 rounded-xl px-3 py-2">
                      <p className="text-xs opacity-90">Period in</p>
                      <p className="text-xl font-bold">{daysUntilPeriod}d</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })()}

          {/* Daily Quests */}
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider px-1">Daily Quests</h2>
            
            <div className="grid gap-3">
              {/* Breakfast Quest */}
              <QuestCard
                icon={Utensils}
                title="Morning Nourishment"
                subtitle={breakfast.name}
                completed={todayCompleted.breakfast}
                onToggle={() => toggleTask("breakfast")}
                color="orange"
              />

              {/* Movement Quest */}
              <QuestCard
                icon={Dumbbell}
                title="Movement Quest"
                subtitle={exercise.title}
                completed={todayCompleted.movement}
                onToggle={() => toggleTask("movement")}
                color="green"
              />

              {/* Water Tracker */}
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <Droplets className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">Hydration</p>
                      <p className="text-sm text-slate-500">{todayWater} / {waterGoal} cups</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => updateWater(Math.max(0, todayWater - 1))}
                      className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600"
                    >
                      -
                    </button>
                    <button
                      onClick={() => updateWater(Math.min(waterGoal + 4, todayWater + 1))}
                      className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white"
                    >
                      +
                    </button>
                  </div>
                </div>
                <div className="flex gap-1">
                  {Array.from({ length: waterGoal }).map((_, i) => (
                    <div
                      key={i}
                      onClick={() => updateWater(i + 1)}
                      className={`flex-1 h-2 rounded-full cursor-pointer transition ${
                        i < todayWater ? "bg-blue-400" : "bg-slate-100"
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Supplement */}
              <QuestCard
                icon={Pill}
                title="Supplements"
                subtitle="Take your daily vitamins"
                completed={todayCompleted.supplement}
                onToggle={() => toggleTask("supplement")}
                color="purple"
              />

              {/* Mindfulness */}
              <QuestCard
                icon={Flower2}
                title="Mindful Moment"
                subtitle="5 min of peace"
                completed={todayCompleted.mindfulness}
                onToggle={() => toggleTask("mindfulness")}
                color="pink"
              />
            </div>
          </div>

          {/* Quick Actions */}
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider px-1">Quick Actions</h2>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setShowMoodCheck(true)}
                className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 text-left hover:border-pink-200 transition"
              >
                <Heart className="w-6 h-6 text-pink-500 mb-2" />
                <p className="font-medium text-slate-800">Log Mood</p>
                <p className="text-xs text-slate-500">How are you feeling?</p>
              </button>

              <button
                onClick={() => setActiveSection("period")}
                className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 text-left hover:border-pink-200 transition"
              >
                <Calendar className="w-6 h-6 text-pink-500 mb-2" />
                <p className="font-medium text-slate-800">Period Tracker</p>
                <p className="text-xs text-slate-500">Cycle insights</p>
              </button>

              <button
                onClick={() => setShowDoctorPrep(true)}
                className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 text-left hover:border-indigo-200 transition"
              >
                <FileText className="w-6 h-6 text-indigo-500 mb-2" />
                <p className="font-medium text-slate-800">Doctor Prep</p>
                <p className="text-xs text-slate-500">Health report</p>
              </button>

              <button
                onClick={() => setActiveSection("cookbook")}
                className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 text-left hover:border-orange-200 transition"
              >
                <Utensils className="w-6 h-6 text-orange-500 mb-2" />
                <p className="font-medium text-slate-800">Recipes</p>
                <p className="text-xs text-slate-500">PCOS-friendly meals</p>
              </button>

              <button
                onClick={() => setActiveSection("exercises")}
                className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 text-left hover:border-green-200 transition"
              >
                <Dumbbell className="w-6 h-6 text-green-500 mb-2" />
                <p className="font-medium text-slate-800">Exercises</p>
                <p className="text-xs text-slate-500">Movement library</p>
              </button>
            </div>
          </div>
        </main>

        {/* Bottom Navigation */}
        <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 z-50">
          <div className="max-w-2xl mx-auto px-4">
            <div className="flex items-center justify-around py-3">
              <NavButton
                icon={Home}
                label="Home"
                active={activeSection === "main"}
                onClick={() => setActiveSection("main")}
              />
              <NavButton
                icon={Calendar}
                label="Period"
                active={activeSection === "period"}
                onClick={() => setActiveSection("period")}
              />
              <NavButton
                icon={BookOpen}
                label="Learn"
                onClick={() => setActiveSection("tips")}
              />
              <NavButton
                icon={Users}
                label="Partner"
                onClick={() => setActiveSection("partner")}
              />
              <NavButton
                icon={User}
                label="Profile"
                onClick={() => setActiveSection("profile")}
              />
            </div>
          </div>
        </nav>

        {/* Modals */}
        <AnimatePresence>
          {showCompanion && <CompanionChat onClose={() => setShowCompanion(false)} />}
          {showMoodCheck && <MoodCheck onClose={() => setShowMoodCheck(false)} />}
          {showDoctorPrep && <DoctorPrep onClose={() => setShowDoctorPrep(false)} />}
        </AnimatePresence>
      </div>
    );
  }

  if (activeSection === "period") {
    return <PeriodTracker onClose={() => setActiveSection("main")} />;
  }

  if (activeSection === "history") {
    return <HistoryView userId={user?.id} onBack={() => setActiveSection("main")} />;
  }

  if (activeSection === "partner") {
    return <PartnerDashboard onBack={() => setActiveSection("main")} />;
  }

  if (activeSection === "profile") {
    return <ProfileSection onBack={() => setActiveSection("main")} />;
  }

  return null;
}
